/* eslint-disable no-unused-vars */
/**
 * FormBuilderDialog — the Form Template Builder, extracted verbatim from
 * App.js (legacy Admin dialog, lines ~26468-29169 + its handlers) as part of
 * the V2 Forms migration (Jun 2026).  UI and behavior are intentionally
 * identical to the legacy builder; only the mount/state plumbing changed:
 * the component owns all builder state and is host-agnostic, so both the
 * legacy Dashboard and the native /v2/admin/forms route mount the same code.
 */
import React, { useState, useEffect, useCallback, useRef, useMemo, useDeferredValue } from 'react';
import { formatDateMDY } from '../../utils/dateFormat';
import axios from 'axios';
import { toast } from 'sonner';
import {
  DndContext, closestCenter, PointerSensor, KeyboardSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, sortableKeyboardCoordinates, verticalListSortingStrategy, useSortable, arrayMove,
} from '@dnd-kit/sortable';
import { useDroppable } from '@dnd-kit/core';
import { CSS } from '@dnd-kit/utilities';
import {
  Plus, X, Save, Eye, ChevronDown, ChevronUp, ChevronLeft, ChevronRight,
  GripVertical, Pencil, Copy, Trash2, Settings, Edit, Upload, FileJson, Bold, Italic, Underline, List, ListOrdered, Type, Palette,
  AlignCenter, AlignLeft, AlignRight, ArrowUpDown, Indent, Info, Link, Outdent,
} from 'lucide-react';

import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import ReminderFieldConfig from './ReminderFieldConfig';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Switch } from '../ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { DebouncedTextarea, DebouncedInput } from '../ui/debounced-textarea';
import { getOptionLabel } from '../../utils/helpers';
import { cn } from '@/lib/utils';
import { staticTextBorderStyle, staticTextImageClass } from '../../utils/staticTextStyle';
import FormTemplatePreviewDialog from './FormTemplatePreviewDialog';
import { useAuth } from '../../context/AuthContext';
import { TaskTypeManagerDialog } from './TaskTypeManagerDialog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Status triggers available for template status notifications
const STATUS_NOTIFICATION_OPTIONS = [
  { value: 'supervisor_signed', label: 'Supervisor Signed (any resulting status)' },
  { value: 'signed', label: 'Signed (Provider)' },
  { value: 'provider_signed', label: 'Provider Signed' },
  { value: 'signed_ready_for_billing', label: 'Signed — Ready for Billing' },
  { value: 'completed', label: 'Completed' },
  { value: 'client_completed', label: 'Client Completed' },
  { value: 'sent_to_client_for_signature', label: 'Sent to Client for Signature' },
  { value: 'sent_to_supervisor', label: 'Sent to Supervisor' },
  { value: 'pending_supervisor_signature', label: 'Pending Supervisor Signature' },
  { value: 'needs_corrections', label: 'Needs Corrections' },
  { value: 'client_declined', label: 'Client Declined' },
];

// Utility function to migrate flat form fields to row groups for grid layout
// Used by Dashboard, PublicPortal, and other components that render forms
const migrateFormFieldsToRowGroups = (formFields) => {
  if (!formFields || formFields.length === 0) {
    return [{ id: 'row_empty_default', fields: [] }];
  }
  
  const rowGroups = [];
  let currentRowFields = [];
  let currentRowWidth = 0;
  
  const createRowId = (fields) => {
    // Create stable row ID based on field IDs in the row
    if (fields.length === 0) return 'row_empty';
    return `row_${fields.map(f => f.id).join('_')}`;
  };
  
  formFields.forEach((field, index) => {
    const fieldWidth = parseInt(field.width || '100');
    
    // Full-width fields (100%) always get their own row
    if (fieldWidth >= 100) {
      // Save current row if it has fields
      if (currentRowFields.length > 0) {
        rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
      }
      // Add full-width field as its own row
      rowGroups.push({ id: createRowId([field]), fields: [field] });
      // Start new row
      currentRowFields = [];
      currentRowWidth = 0;
    } else if (currentRowWidth + fieldWidth > 100) {
      // Would exceed 100%, start new row
      if (currentRowFields.length > 0) {
        rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
      }
      currentRowFields = [field];
      currentRowWidth = fieldWidth;
    } else {
      // Add to current row
      currentRowFields.push(field);
      currentRowWidth += fieldWidth;
    }
  });
  
  // Don't forget the last row
  if (currentRowFields.length > 0) {
    rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
  }
  
  // Ensure at least one empty row if nothing was added
  if (rowGroups.length === 0) {
    rowGroups.push({ id: 'row_empty_default', fields: [] });
  }
  
  return rowGroups;
};

// Centralized Client Status List
// MOVED TO: /src/constants/index.js - CLIENT_STATUSES
// DELETE THIS COMMENT AFTER VERIFYING BUILD

// Memoized field label input to prevent focus loss during typing
// Uses DebouncedInput for autosave with debounce delay
const FieldLabelInput = React.memo(({ fieldId, initialValue, onChange, className, placeholder }) => {
  const handleChange = useCallback((e) => {
    onChange(fieldId, { label: e.target.value });
  }, [fieldId, onChange]);
  
  return (
    <DebouncedInput
      value={initialValue || ''}
      onChange={handleChange}
      debounceMs={400}
      className={cn(
        "flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-base shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50 md:text-sm",
        className
      )}
      placeholder={placeholder}
    />
  );
}, (prevProps, nextProps) => {
  // Re-render if fieldId changes OR if initialValue changes externally
  return prevProps.fieldId === nextProps.fieldId && prevProps.initialValue === nextProps.initialValue;
});
FieldLabelInput.displayName = 'FieldLabelInput';

// Sortable Field Item Component for Drag and Drop
// Manages its own expand state to prevent re-renders from parent
// NOTE: We accept children for expanded content but use custom memo comparison
// to prevent re-renders when only children reference changes
const SortableFieldItem = React.memo(({ 
  field, 
  availableFieldTypes, 
  updateFormField, 
  removeFormField, 
  duplicateFormField, 
  moveFieldToSection, 
  allSections, 
  currentSectionId, 
  children,  // Accept children for expanded content
  renderExpandedContent  // Legacy prop - prefer children
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: field.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  // Check if field is non-editable (auto-populated data)
  // client_diagnoses_selector is interactive, not auto-populated
  const fieldTypeInfo = availableFieldTypes.find(t => t.type === field.type);
  const isNonEditable = field.type !== 'client_diagnoses_selector' && field.type !== 'medication_plan' && field.type !== 'add_contacts' && field.type !== 'pan_select' && (
    ['client_data', 'org_data', 'clinical', 'special'].includes(fieldTypeInfo?.category) || 
    field.type?.startsWith('client_') || 
    ['service_dropdown', 'group_dropdown', 'treatment_plan_goals', 'consent_affirmation'].includes(field.type)
  );

  // Render WYSIWYG preview of the field component
  const renderFieldPreview = () => {
    // Non-editable fields get special purple styling
    if (isNonEditable) {
      return (
        <div className="flex-1 h-8 bg-purple-50 rounded border border-purple-200 flex items-center px-3 text-xs text-purple-700">
          <span className="mr-2">📥</span>
          {field.label || fieldTypeInfo?.label || field.type}
          <span className="ml-auto text-purple-400 text-[10px]">auto-populated</span>
        </div>
      );
    }

    switch (field.type) {
      case 'text':
      case 'email':
      case 'phone':
        return <Input disabled placeholder={field.label || 'Text input'} className="h-8 text-sm bg-white flex-1" />;
      
      case 'textarea':
        return <DebouncedTextarea disabled placeholder={field.label || 'Text area'} className="h-10 text-sm bg-white flex-1 resize-none" rows={1} />;
      
      case 'date':
        return <Input type="date" disabled className="h-8 text-sm bg-white flex-1" />;
      
      case 'time':
        return <Input type="time" disabled className="h-8 text-sm bg-white flex-1" />;
      
      case 'select':
        return (
          <Select disabled>
            <SelectTrigger className="h-8 text-sm bg-white flex-1">
              <SelectValue placeholder={getOptionLabel(field.options?.[0]) || 'Select option'} />
            </SelectTrigger>
          </Select>
        );

      case 'pan_select':
        return (
          <div className="flex-1 flex items-center gap-2 min-h-[32px]">
            <Select disabled>
              <SelectTrigger className="h-8 text-sm bg-white flex-1">
                <SelectValue placeholder="Select existing PAN or write custom need…" />
              </SelectTrigger>
            </Select>
            <span className="text-[10px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-1.5 py-0.5 whitespace-nowrap">→ Treatment Plan</span>
          </div>
        );
      
      case 'multiselect':
        return (
          <div className="flex flex-wrap gap-1 flex-1 items-center min-h-[32px]">
            {(field.options || ['Option 1', 'Option 2']).slice(0, 4).map((opt, i) => (
              <span key={i} className="inline-flex items-center px-2 py-0.5 text-xs bg-blue-100 text-blue-800 rounded">
                ☐ {getOptionLabel(opt)}
              </span>
            ))}
            {(field.options?.length || 0) > 4 && <span className="text-xs text-gray-400">+{field.options.length - 4} more</span>}
          </div>
        );
      
      case 'checkbox':
        return (
          <div className="flex items-center gap-2 h-8">
            <input type="checkbox" disabled className="h-4 w-4" />
            <span className="text-sm text-gray-600">{field.label || 'Checkbox'}</span>
          </div>
        );
      
      case 'checkbox_group':
        return (
          <div className="flex flex-wrap gap-x-4 gap-y-1 flex-1 items-center min-h-[32px]">
            {(field.options || ['Option 1', 'Option 2']).slice(0, 4).map((opt, i) => (
              <label key={i} className="inline-flex items-center text-sm">
                <input type="checkbox" disabled className="h-3.5 w-3.5 mr-1" />
                {getOptionLabel(opt)}
              </label>
            ))}
            {(field.options?.length || 0) > 4 && <span className="text-xs text-gray-400">+{field.options.length - 4} more</span>}
          </div>
        );
      
      case 'radio':
        return (
          <div className="flex flex-wrap gap-x-4 gap-y-1 flex-1 items-center min-h-[32px]">
            {(field.options || ['Option 1', 'Option 2']).slice(0, 4).map((opt, i) => (
              <label key={i} className="inline-flex items-center text-sm">
                <input type="radio" disabled name={`preview-${field.id}`} className="h-3.5 w-3.5 mr-1" />
                {getOptionLabel(opt)}
              </label>
            ))}
            {(field.options?.length || 0) > 4 && <span className="text-xs text-gray-400">+{field.options.length - 4} more</span>}
          </div>
        );
      
      case 'heading':
        const HeadingTag = field.headingLevel || 'h2';
        const headingStyles = {
          h1: 'text-xl font-bold',
          h2: 'text-lg font-semibold',
          h3: 'text-base font-medium'
        };
        return (
          <span 
            className={`${headingStyles[HeadingTag] || headingStyles.h2} flex-1`}
            style={{ color: field.fontColor || '#000', textAlign: field.textAlign || 'left' }}
          >
            {field.label || 'Heading'}
          </span>
        );
      
      case 'static_text':
        // Show content preview, truncated if too long
        const staticContent = typeof field.content === 'string' ? field.content : '';
        // Strip HTML tags for preview display
        const strippedContent = staticContent.replace(/<[^>]+>/g, '');
        const staticPreview = strippedContent.length > 100 ? strippedContent.substring(0, 100) + '...' : strippedContent;
        return (
          <div className="flex-1 bg-gray-50 border border-gray-200 rounded p-2">
            {staticPreview ? (
              <span className="text-sm text-gray-600 whitespace-pre-wrap">{staticPreview}</span>
            ) : (
              <span className="text-sm text-gray-400 italic">Click to add static text content...</span>
            )}
          </div>
        );
      
      case 'divider':
        return <hr className="flex-1 border-gray-300" />;
      
      case 'client_visibility_divider':
        return (
          <div className="flex-1 flex items-center justify-center py-2">
            <div className="flex-1 border-t-2 border-dashed border-teal-400"></div>
            <span className="px-3 text-xs text-teal-600 font-medium whitespace-nowrap">✂️ CLIENT SUMMARY START</span>
            <div className="flex-1 border-t-2 border-dashed border-teal-400"></div>
          </div>
        );
      
      case 'spacer':
        return <div className="flex-1 h-6 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-400">Spacer</div>;
      
      case 'signature':
        return <div className="flex-1 h-8 bg-gray-100 rounded border border-dashed border-gray-300 flex items-center justify-center text-xs text-gray-500">✍️ Signature</div>;
      
      case 'send_assessment':
        return <div className="flex-1 h-8 bg-purple-50 rounded border border-dashed border-purple-300 flex items-center justify-center text-xs text-purple-600">📤 {field.label || 'Send Assessment'} (prompt at note time)</div>;
      
      case 'client_diagnoses_selector':
        return (
          <div className="flex-1 h-8 bg-teal-50 rounded border border-teal-200 flex items-center px-3 text-xs text-teal-700">
            <span className="mr-2">☑️</span>
            {field.label || 'Client Diagnoses (Selectable)'}
            <span className="ml-auto text-teal-500 text-[10px]">select from client diagnoses</span>
          </div>
        );
      
      default:
        return <div className="flex-1 h-8 bg-gray-100 rounded border flex items-center px-2 text-xs text-gray-500">{field.label || field.type}</div>;
    }
  };

  // Background color based on field type
  const bgClass = isNonEditable ? 'bg-purple-50 border-purple-200' : 'bg-gray-50';

  return (
    <div ref={setNodeRef} style={style} className={`border rounded ${bgClass} ${isExpanded ? 'p-3' : 'p-2'}`}>
      {/* Two-row WYSIWYG Layout */}
      <div className="space-y-1.5">
        {/* Row 1: Field Label + Actions */}
        <div className="flex items-center gap-2">
          {/* Drag Handle */}
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing flex-shrink-0">
            <GripVertical className="h-4 w-4 text-gray-400 hover:text-gray-600" />
          </div>
          
          {/* Field Label - Editable (wider now) */}
          {/* Field type badge - shown for ALL field types */}
          <span className={`text-[10px] uppercase tracking-wide px-1.5 py-0.5 rounded flex-shrink-0 ${
            isNonEditable ? 'text-purple-500 bg-purple-50' : 'text-gray-400 bg-gray-100'
          }`}>
            {fieldTypeInfo?.label || field.type}
          </span>
          
          {/* Field Label - Editable for most fields */}
          {!['divider', 'spacer', 'client_visibility_divider'].includes(field.type) && !isNonEditable && (
            <FieldLabelInput
              fieldId={field.id}
              initialValue={field.label}
              onChange={updateFormField}
              className={`font-medium text-sm h-7 flex-1 ${field.type === 'heading' ? 'font-bold text-base' : ''}`}
              placeholder={field.type === 'heading' ? 'Heading text...' : field.type === 'static_text' ? 'Static text label...' : `Enter ${(fieldTypeInfo?.label || field.type || 'field').toLowerCase()} label...`}
            />
          )}
          {/* Non-editable fields show label as static text */}
          {isNonEditable && (
            <span className="font-medium text-sm flex-1 text-purple-700">
              {field.label || fieldTypeInfo?.label || field.type}
            </span>
          )}
          {/* Layout-only fields (divider, spacer) - no label needed */}
          {['divider', 'spacer', 'client_visibility_divider'].includes(field.type) && !isNonEditable && (
            <span className="font-medium text-sm flex-1 text-gray-500">
              {field.label || fieldTypeInfo?.label || field.type}
            </span>
          )}
          
          {/* Required indicator */}
          {field.required && !['divider', 'spacer', 'heading', 'static_text', 'client_visibility_divider'].includes(field.type) && (
            <span className="text-red-500 text-sm font-bold flex-shrink-0" title="Required">*</span>
          )}
          
          {/* Action Buttons - always visible, won't wrap */}
          <div className="flex items-center gap-0.5 flex-shrink-0 ml-auto">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => duplicateFormField(field.id)}
              type="button"
              className="h-6 w-6 p-0 text-blue-600 hover:text-blue-700 hover:bg-blue-50"
              title="Duplicate Field"
            >
              <Copy className="h-3 w-3" />
            </Button>
            
            {/* Move to Section - Icon with dropdown */}
            {allSections && allSections.length > 1 && (
              <div className="relative">
                <select
                  className="absolute inset-0 opacity-0 cursor-pointer w-6 h-6"
                  onChange={(e) => {
                    if (e.target.value && e.target.value !== currentSectionId) {
                      moveFieldToSection(field.id, e.target.value);
                    }
                  }}
                  value=""
                  title="Move to section"
                >
                  <option value="">Move field...</option>
                  {allSections.filter(s => s.header.id !== currentSectionId).map(section => (
                    <option key={section.header.id} value={section.header.id}>
                      {section.header.label || 'Fields'}
                    </option>
                  ))}
                </select>
                <Button
                  size="sm"
                  variant="ghost"
                  type="button"
                  className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-100 pointer-events-none"
                  title="Move to section"
                >
                  <ArrowUpDown className="h-3 w-3" />
                </Button>
              </div>
            )}
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => removeFormField(field.id)}
              type="button"
              className="h-6 w-6 p-0 text-red-500 hover:text-red-600 hover:bg-red-50"
              title="Remove Field"
            >
              <X className="h-3 w-3" />
            </Button>
            
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setIsExpanded(prev => !prev)}
              type="button"
              className="h-6 w-6 p-0 text-gray-500 hover:text-gray-700 hover:bg-gray-200"
              title={isExpanded ? 'Collapse options' : 'Expand options'}
            >
              {isExpanded ? <ChevronUp className="h-3.5 w-3.5" /> : <ChevronDown className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
        
        {/* Row 2: WYSIWYG Field Preview */}
        <div className="pl-6">
          {renderFieldPreview()}
        </div>
      </div>
      
      {/* Expanded Content - Additional Options */}
      {isExpanded && (
        <div className="mt-3 pt-3 border-t">
          {/* Required checkbox - in expanded mode */}
          {!['divider', 'spacer', 'heading', 'static_text', 'client_visibility_divider'].includes(field.type) && (
            <div className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                checked={field.required}
                onChange={(e) => updateFormField(field.id, { required: e.target.checked })}
                className="h-4 w-4"
                id={`required-${field.id}`}
              />
              <label htmlFor={`required-${field.id}`} className="text-xs font-medium cursor-pointer">Required Field</label>
            </div>
          )}
          {/* Set a Reminder — full config editor */}
          {field.type === 'set_a_reminder' && (
            <div className="mb-3">
              <ReminderFieldConfig field={field} updateFormField={updateFormField} />
            </div>
          )}
          {/* Carry Forward toggle - value-bearing input fields only */}
          {['text', 'textarea', 'email', 'phone', 'date', 'time', 'select', 'multiselect', 'checkbox', 'checkbox_group', 'radio', 'matrix'].includes(field.type) && (
            <div className="flex items-center space-x-2 mb-3">
              <input
                type="checkbox"
                checked={!!field.is_carry_forward}
                onChange={(e) => updateFormField(field.id, { is_carry_forward: e.target.checked })}
                className="h-4 w-4"
                id={`carry-forward-${field.id}`}
                data-testid={`carry-forward-toggle-${field.id}`}
              />
              <label htmlFor={`carry-forward-${field.id}`} className="text-xs font-medium cursor-pointer">
                Carry Forward
                <span className="text-gray-500 font-normal ml-1">(pre-fill from this client's most recent completed note by the same provider)</span>
              </label>
            </div>
          )}
          {/* Render children (preferred) or legacy renderExpandedContent callback */}
          {children}
          {renderExpandedContent && renderExpandedContent(field, updateFormField)}
        </div>
      )}
    </div>
  );
});
SortableFieldItem.displayName = 'SortableFieldItem';

// Sortable Section Component for Drag and Drop
const SortableSection = ({ section, availableFieldTypes, updateFormField, removeFormField, duplicateFormField, moveFieldToSection, allSections, children }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isEditingLabel, setIsEditingLabel] = useState(false);
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: section.header.id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const sectionColor = section.header.backgroundColor || '#3b82f6'; // Default blue

  return (
    <div ref={setNodeRef} className="border-2 rounded-lg p-4" 
      style={{ 
        ...style, 
        borderColor: sectionColor,
        backgroundColor: `${sectionColor}10`
      }}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center space-x-2 flex-1">
          <div {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing">
            <GripVertical className="h-6 w-6" style={{ color: sectionColor }} />
          </div>
          {isEditingLabel ? (
            <DebouncedInput
              value={section.header.label || ''}
              onChange={(e) => updateFormField(section.header.id, { label: e.target.value })}
              onBlur={() => setIsEditingLabel(false)}
              onKeyDown={(e) => e.key === 'Enter' && setIsEditingLabel(false)}
              autoFocus
              className="font-semibold text-sm uppercase tracking-wide bg-white border rounded px-2 py-1 flex-1 min-h-[32px]"
              style={{ borderColor: sectionColor, color: sectionColor }}
              placeholder="Section Name (optional)"
            />
          ) : (
            <div 
              className="font-semibold text-sm uppercase tracking-wide cursor-pointer hover:opacity-80 px-2 py-1 rounded min-h-[32px] min-w-[150px] flex items-center gap-2 border border-transparent hover:border-gray-300 transition-all"
              style={{ color: sectionColor }}
              onClick={() => setIsEditingLabel(true)}
              title="Click to edit section name"
            >
              📋 {section.header.label || '(Click to name section)'}
              <Pencil className="h-3 w-3 opacity-50" />
            </div>
          )}
          <Badge style={{ backgroundColor: `${sectionColor}20`, color: sectionColor }}>
            {section.fields.length} field{section.fields.length !== 1 ? 's' : ''}
          </Badge>
          {/* Color picker */}
          <div className="flex items-center gap-1">
            <input
              type="color"
              value={sectionColor}
              onChange={(e) => updateFormField(section.header.id, { backgroundColor: e.target.value })}
              className="w-6 h-6 rounded cursor-pointer border-0"
              title="Section color"
            />
          </div>
        </div>
        <div className="flex items-center space-x-2">
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setIsCollapsed(!isCollapsed)}
            type="button"
          >
            {isCollapsed ? <ChevronDown className="h-4 w-4" /> : <ChevronUp className="h-4 w-4" />}
          </Button>
          {section.header.id !== 'default' && (
            <Button
              size="sm"
              variant="outline"
              onClick={() => removeFormField(section.header.id)}
              type="button"
              className="text-red-500"
            >
              ✕
            </Button>
          )}
        </div>
      </div>
      {!isCollapsed && (
        <div className="space-y-2 ml-8">
          {children}
        </div>
      )}
    </div>
  );
};

// Droppable Row Component for Form Builder - allows dropping fields into rows
const DroppableRow = ({ rowId, rowIndex, children, isEmpty }) => {
  const { isOver, setNodeRef } = useDroppable({
    id: rowId,
  });
  
  return (
    <div 
      ref={setNodeRef}
      className={`relative border border-dashed rounded-lg p-2 transition-colors ${
        isOver 
          ? 'border-blue-500 bg-blue-50' 
          : isEmpty 
            ? 'border-gray-300 bg-gray-50/50' 
            : 'border-gray-300 bg-gray-50/30'
      }`}
    >
      {/* Row Label */}
      <div className={`absolute -top-2 left-2 px-1 text-xs ${isOver ? 'bg-blue-50 text-blue-600' : 'bg-white text-gray-400'}`}>
        Row {rowIndex + 1} {isOver && '(Drop here)'}
      </div>
      
      {children}
    </div>
  );
};


const availableFieldTypes = [
  // Input Fields
  { type: 'text', label: 'Text Input', icon: '📝', category: 'input' },
  { type: 'textarea', label: 'Text Area', icon: '📄', category: 'input' },
  { type: 'email', label: 'Email', icon: '📧', category: 'input' },
  { type: 'phone', label: 'Phone', icon: '📞', category: 'input' },
  { type: 'date', label: 'Date', icon: '📅', category: 'input' },
  { type: 'time', label: 'Time', icon: '🕐', category: 'input' },
  { type: 'select', label: 'Dropdown', icon: '⬇️', category: 'input' },
  { type: 'multiselect', label: 'Multi-Select Dropdown', icon: '☰', category: 'input' },
  { type: 'checkbox', label: 'Checkbox', icon: '☑️', category: 'input' },
  { type: 'checkbox_group', label: 'Checkbox Group', icon: '☑️', category: 'input' },
  { type: 'radio', label: 'Radio Buttons', icon: '🔘', category: 'input' },
  { type: 'matrix', label: 'Matrix/Grid', icon: '⊞', category: 'input' },
  { type: 'file', label: 'File Upload', icon: '📎', category: 'input' },
  { type: 'signature', label: 'Signature Field', icon: '✍️', category: 'input' },
  { type: 'send_assessment', label: 'Send Assessment', icon: '📤', category: 'input' },
  // Layout & Formatting
  { type: 'heading', label: 'Heading Text', icon: '📌', category: 'layout' },
  { type: 'section_header', label: 'Section Header', icon: '📋', category: 'layout' },
  { type: 'static_text', label: 'Static Text', icon: '📃', category: 'layout' },
  { type: 'divider', label: 'Horizontal Line', icon: '➖', category: 'layout' },
  { type: 'client_visibility_divider', label: 'Client Summary Divider', icon: '✂️', category: 'layout' },
  { type: 'spacer', label: 'Vertical Space', icon: '⬍', category: 'layout' },
  { type: 'container', label: 'Shaded Box', icon: '▢', category: 'layout' },
  { type: 'row_placeholder', label: 'Empty Row', icon: '⊡', category: 'layout' },
  // Client Data Fields (Auto-fill)
  { type: 'client_name', label: 'Client Name', icon: '👤', category: 'client_data' },
  { type: 'client_dob', label: 'Date of Birth', icon: '🎂', category: 'client_data' },
  { type: 'client_age', label: 'Client Age', icon: '🔢', category: 'client_data' },
  { type: 'client_email', label: 'Email Address', icon: '📧', category: 'client_data' },
  { type: 'client_phone', label: 'Phone Number', icon: '📞', category: 'client_data' },
  { type: 'client_address', label: 'Full Address', icon: '🏠', category: 'client_data' },
  { type: 'client_diagnoses', label: 'Client Diagnoses (All)', icon: '🏥', category: 'client_data' },
  { type: 'client_diagnoses_selector', label: 'Client Diagnoses (Selectable)', icon: '☑️', category: 'client_data' },
  { type: 'client_medications', label: 'Medications Table', icon: '💊', category: 'client_data' },
  { type: 'client_allergies', label: 'Allergies', icon: '⚠️', category: 'client_data' },
  { type: 'client_insurance', label: 'Insurance Info', icon: '🏥', category: 'client_data' },
  { type: 'client_providers', label: 'Assigned Providers', icon: '👨‍⚕️', category: 'client_data' },
  { type: 'client_intake_date', label: 'Date of Intake', icon: '📅', category: 'client_data' },
  { type: 'client_last_appointment', label: 'Most Recent Appointment', icon: '📆', category: 'client_data' },
  { type: 'client_next_appointment', label: 'Next Appointment', icon: '📅', category: 'client_data' },
  { type: 'client_vitals', label: 'Most Recent Vitals', icon: '❤️', category: 'client_data' },
  { type: 'add_contacts', label: 'Add Contact (to chart)', icon: '📇', category: 'client_data' },
  // Organization Data Fields (Auto-fill)
  { type: 'service_dropdown', label: 'Service Selection', icon: '🎯', category: 'org_data' },
  { type: 'location_dropdown', label: 'Location Selection', icon: '📍', category: 'org_data' },
  { type: 'provider_dropdown', label: 'Provider Selection', icon: '👨‍⚕️', category: 'org_data' },
  { type: 'group_dropdown', label: 'Group Selection', icon: '👥', category: 'org_data' },
  { type: 'current_provider_name', label: 'Current Provider Name', icon: '👤', category: 'org_data' },
  { type: 'current_user_name', label: 'Current User Name', icon: '🧑‍💻', category: 'org_data' },
  { type: 'current_date', label: 'Today\'s Date', icon: '📅', category: 'org_data' },
  // Certificate Fields
  { type: 'certificate_title', label: 'Certificate Title', icon: '🎓', category: 'certificate' },
  { type: 'certificate_recipient', label: 'Recipient Name', icon: '👤', category: 'certificate' },
  { type: 'certificate_completion_text', label: 'Completion Text', icon: '📜', category: 'certificate' },
  { type: 'certificate_signature_line', label: 'Signature Line', icon: '✍️', category: 'certificate' },
  { type: 'certificate_date', label: 'Certificate Date', icon: '📅', category: 'certificate' },
  { type: 'certificate_logo', label: 'Organization Logo', icon: '🏢', category: 'certificate' },
  // Special Fields
  { type: 'insurance', label: 'Insurance Info', icon: '🏥', category: 'special' },
  { type: 'emergency_contact', label: 'Emergency Contact', icon: '🚨', category: 'special' },
  // Clinical Components (Progress Notes)
  { type: 'treatment_plan_goals', label: 'Treatment Plan Goals', icon: '🎯', category: 'clinical' },
  { type: 'pan_select', label: 'PAN Selector (feeds Treatment Plan)', icon: '🧭', category: 'clinical' },
  { type: 'consent_affirmation', label: 'Consent Affirmation', icon: '✅', category: 'clinical' },
  { type: 'set_a_reminder', label: 'Set a Reminder', icon: '⏰', category: 'clinical' },
  { type: 'medication_plan', label: 'Medication Plan', icon: '💊', category: 'clinical' },
  // Workflow Triggers (Feb 2026)
  // create_task generates a real Task on note save — used for
  // reschedule requests, lab-orders, follow-up needs.  Field
  // designer configures default assignee_type + due offset.
  { type: 'create_task', label: 'Create Task on Save', icon: '📋', category: 'clinical' }
];


// Legacy scored-assessment templates (PHQ-9 era) store fields as
// {field_id, field_name, field_type} with score_values. Normalize additively
// for the builder (id/type/label) — original keys are preserved so portal
// rendering (field_type-first) and scoring (score_values) keep working.
const normalizeLegacyAssessmentFields = (fields) => (fields || []).map((f) => {
  if (!f || f.type || !f.field_type) return f;
  const t = f.field_type === 'label' ? 'static_text' : f.field_type;
  return {
    ...f,
    id: f.id || f.field_id,
    type: t,
    label: f.label || (f.field_type === 'label' ? '' : f.field_name) || '',
    ...(t === 'static_text' ? { content: f.content || f.field_name || '' } : {}),
  };
});

// On save, mirror builder edits back onto the legacy keys.
const syncLegacyAssessmentKeys = (fields) => (fields || []).map((f) => {
  if (!f || !f.field_id || !f.field_type) return f;
  return {
    ...f,
    field_name: f.type === 'static_text' ? (f.content || f.field_name) : (f.label || f.field_name),
  };
});

export default function FormBuilderDialog({
  open,
  onOpenChange,
  editingTemplate: editingTemplateProp,
  initialData,
  initialFields,
  formTypes = [],
  setFormTemplates = () => {},
  fetchFormTemplates = () => {},
  onCancelToManagement,
  headerExtras = null,
}) {
  const { user } = useAuth();

  // ── Builder state (moved verbatim from App.js) ────────────────────
  const [formTemplateData, setFormTemplateData] = useState({
    name: '', description: '', form_type: '', fields: [], is_active: true, is_group_template: false,
  });
  const [activeFormFields, setActiveFormFields] = useState([]);
  const [activeFormSections, setActiveFormSections] = useState([
    { id: 'section_1', title: 'General Information', rowGroups: [{ id: 'row_1', fields: [] }], fields: [] }
  ]);
  const [expandedFieldIds, setExpandedFieldIds] = useState({});
  const [editingFormTemplate, setEditingFormTemplate] = useState(null);
  const [showFormPreview, setShowFormPreview] = useState(false);
  const [previewMode, setPreviewMode] = useState('edit');
  // Merge any unsaved static-text editor cache into fields for the preview
  const buildPreviewFields = () => activeFormFields.map(field => {
    if (field.type === 'static_text' && staticTextEditorCache.current.has(field.id)) {
      return { ...field, content: staticTextEditorCache.current.get(field.id) };
    }
    return field;
  });
  const [isFieldPaletteCollapsed, setIsFieldPaletteCollapsed] = useState(false);
  const [formHeaderCollapsed, setFormHeaderCollapsed] = useState(true);
  const [fieldContextMenu, setFieldContextMenu] = useState({ show: false, x: 0, y: 0, fieldType: null });
  const staticTextEditorCache = useRef(new Map());
  const staticTextSelectionRef = useRef(null);
  const activeFormFieldsRef = useRef(activeFormFields);
  useEffect(() => { activeFormFieldsRef.current = activeFormFields; }, [activeFormFields]);
  const formTemplateDataRef = useRef(formTemplateData);
  useEffect(() => { formTemplateDataRef.current = formTemplateData; }, [formTemplateData]);
  const deferredFormFields = useDeferredValue(activeFormFields);

  // Seed internal state from the host every time the dialog opens.
  useEffect(() => {
    if (!open) return;
    setEditingFormTemplate(editingTemplateProp || null);
    setFormTemplateData(initialData || {
      name: '', description: '', form_type: '', fields: [], is_active: true, is_group_template: false,
    });
    setActiveFormFields(normalizeLegacyAssessmentFields(initialFields || (editingTemplateProp?.form_fields ?? [])));
    staticTextEditorCache.current.clear();
    setShowFormPreview(false);
    setFieldContextMenu({ show: false, x: 0, y: 0, fieldType: null });
    
  }, [open]);

  const handleOpenChange = (v) => { if (onOpenChange) onOpenChange(v); };

  // Light lookups used by group_dropdown option seeding + create_task assignee config
  const [clientGroups, setClientGroups] = useState([]);
  const [users, setUsers] = useState([]);
  // Jun 2026 — admin-configurable task types for create_task fields.
  const [taskTypes, setTaskTypes] = useState([]);
  const [showTaskTypeManager, setShowTaskTypeManager] = useState(false);
  const fetchTaskTypes = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/form-tasks/task-types`);
      setTaskTypes(r.data?.task_types || []);
    } catch { setTaskTypes([]); }
  }, []);
  useEffect(() => { if (open) fetchTaskTypes(); }, [open, fetchTaskTypes]);
  useEffect(() => {
    if (!open) return;
    axios.get(`${API}/client-groups`).then(r => setClientGroups(Array.isArray(r.data) ? r.data : (r.data?.groups || []))).catch(() => {});
    axios.get(`${API}/users`).then(r => setUsers(Array.isArray(r.data) ? r.data : (r.data?.users || []))).catch(() => {});
  }, [open]);

  // Status Notifications (Jun 2026) — users eligible as notification recipients
  const activeStaff = useMemo(
    () => (users || []).filter(u => u.is_active !== false && (u.role || '').toLowerCase() !== 'client'),
    [users]
  );
  const updateStatusNotificationRule = (idx, updates) => setFormTemplateData(prev => ({
    ...prev,
    status_notifications: (prev.status_notifications || []).map((r, i) => i === idx ? { ...r, ...updates } : r),
  }));

  const replaceClientVariables = (text, clientData) => {
    if (typeof text !== 'string') return '';
    if (!text || !clientData) return text;
    
    // Use case-insensitive regex with word boundaries to catch variables regardless of surrounding characters
    let result = text;
    
    // Helper to safely replace - handles cases where variable is adjacent to punctuation
    const safeReplace = (str, varName, value) => {
      // Match {{variable}} with optional whitespace inside braces
      const regex = new RegExp(`\\{\\{\\s*${varName}\\s*\\}\\}`, 'gi');
      return str.replace(regex, value || '');
    };
    
    result = safeReplace(result, 'client_name', clientData.full_name);
    result = safeReplace(result, 'client_first_name', clientData.first_name);
    result = safeReplace(result, 'client_last_name', clientData.last_name);
    result = safeReplace(result, 'client_dob', formatDateMDY(clientData.date_of_birth || clientData.dob));
    result = safeReplace(result, 'client_age', (clientData.date_of_birth || clientData.dob) ? Math.floor((new Date() - new Date(clientData.date_of_birth || clientData.dob)) / (365.25 * 24 * 60 * 60 * 1000)).toString() : '');
    result = safeReplace(result, 'client_email', clientData.email);
    result = safeReplace(result, 'client_phone', clientData.phone);
    result = safeReplace(result, 'client_address', [clientData.address || clientData.street || clientData.address_line1, clientData.address_line2, clientData.city, clientData.state, clientData.zipcode || clientData.zip || clientData.zip_code].filter(Boolean).join(', '));
    result = safeReplace(result, 'client_streetaddress', clientData.street || clientData.address || clientData.address_line1);
    result = safeReplace(result, 'client_city', clientData.city);
    result = safeReplace(result, 'client_state', clientData.state);
    result = safeReplace(result, 'client_zipcode', clientData.zipcode || clientData.zip || clientData.zip_code);
    result = safeReplace(result, 'client_SSNunmasked', clientData.ssn);
    result = safeReplace(result, 'client_mrn', clientData.mrn);
    result = safeReplace(result, 'today', new Date().toLocaleDateString());
    result = safeReplace(result, 'provider_name', user?.name);
    result = safeReplace(result, 'current_date', new Date().toLocaleDateString());
    
    return result;
  };


  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: {
        distance: 8, // Minimum drag distance before activation
      },
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Handle dragging fields (including between sections)
  const handleDragEnd = (event) => {
    const { active, over } = event;

    if (active.id !== over.id) {
      setActiveFormFields((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };
  
  // Enhanced drag handler for row-aware drag and drop
  const handleRowDragEnd = (event) => {
    const { active, over } = event;
    
    if (!over) return;
    
    const activeId = active.id;
    const overId = over.id;
    
    if (activeId === overId) return;
    
    // Check if dropping onto a row (row IDs start with "row_")
    const isDropOnRow = String(overId).startsWith('row_');
    
    setActiveFormFields((items) => {
      const activeIndex = items.findIndex((item) => item.id === activeId);
      
      if (activeIndex === -1) return items;
      
      if (isDropOnRow) {
        // Dropping onto a row - need to find where in the flat array this row's fields are
        const sections = groupFieldsIntoSectionsAndRows(items);
        
        // Find which row we're dropping into
        let targetRowFields = [];
        let insertAfterIndex = -1;
        
        for (const section of sections) {
          for (const row of section.rowGroups || []) {
            if (row.id === overId) {
              targetRowFields = row.fields;
              // Insert at the end of this row's fields
              if (targetRowFields.length > 0) {
                const lastFieldInRow = targetRowFields[targetRowFields.length - 1];
                insertAfterIndex = items.findIndex(item => item.id === lastFieldInRow.id);
              } else {
                // Empty row - find where this row would be in the sequence
                // For now, just keep the field where it is
                insertAfterIndex = activeIndex;
              }
              break;
            }
          }
        }
        
        if (insertAfterIndex !== -1 && insertAfterIndex !== activeIndex) {
          // Move the field
          const newItems = [...items];
          const [movedField] = newItems.splice(activeIndex, 1);
          
          // Adjust insert index if we removed from before the insert point
          const adjustedIndex = activeIndex < insertAfterIndex ? insertAfterIndex : insertAfterIndex + 1;
          newItems.splice(adjustedIndex, 0, movedField);
          
          // Apply smart auto-split to the target row
          // First, recalculate sections with the new arrangement
          const newSections = groupFieldsIntoSectionsAndRows(newItems);
          
          // Find the row containing our moved field and apply smart split
          for (const section of newSections) {
            for (const row of section.rowGroups || []) {
              const fieldInRow = row.fields.find(f => f.id === movedField.id);
              if (fieldInRow && row.fields.length > 1) {
                // Apply smart split to this row
                const smartWidths = calculateSmartWidths(row.fields.length);
                row.fields.forEach((f, idx) => {
                  const fieldIdx = newItems.findIndex(item => item.id === f.id);
                  if (fieldIdx !== -1) {
                    newItems[fieldIdx] = { ...newItems[fieldIdx], width: smartWidths[idx] };
                  }
                });
                toast.success(`Field moved! Row adjusted to ${smartWidths.join('/')}% layout`);
                break;
              }
            }
          }
          
          return newItems;
        }
      } else {
        // Dropping onto another field - standard reorder
        const overIndex = items.findIndex((item) => item.id === overId);
        if (overIndex !== -1) {
          return arrayMove(items, activeIndex, overIndex);
        }
      }
      
      return items;
    });
  };

  // Group fields by sections for the form builder UI
  const groupFieldsBySections = (fields) => {
    const sections = [];
    let currentSection = null;
    
    fields.forEach((field) => {
      if (field.type === 'section_header') {
        // Start a new section
        currentSection = {
          header: field,
          fields: []
        };
        sections.push(currentSection);
      } else {
        // Add field to current section or create a default section
        if (!currentSection) {
          currentSection = {
            header: { id: 'default', type: 'section_header', label: 'Fields' },
            fields: []
          };
          sections.push(currentSection);
        }
        currentSection.fields.push(field);
      }
    });
    
    return sections;
  };
  
  // Enhanced grouping: Sections → Row Groups → Fields
  // This is used for display in form builder and rendering
  const groupFieldsIntoSectionsAndRows = (fields) => {
    const sections = [];
    let currentSection = null;
    
    fields.forEach((field) => {
      if (field.type === 'section_header') {
        // Start a new section with empty rowGroups
        currentSection = {
          header: field,
          fields: [], // Keep for compatibility
          rowGroups: []
        };
        sections.push(currentSection);
      } else {
        // Add field to current section or create a default section
        if (!currentSection) {
          currentSection = {
            header: { id: 'default', type: 'section_header', label: 'Fields' },
            fields: [],
            rowGroups: []
          };
          sections.push(currentSection);
        }
        currentSection.fields.push(field);
      }
    });
    
    // Now convert each section's fields into rowGroups
    sections.forEach(section => {
      section.rowGroups = migrateFormFieldsToRowGroups(section.fields);
    });
    
    return sections;
  };
  
  // Flatten sections with rowGroups back to flat array for saving
  const flattenSectionsWithRowGroups = (sections) => {
    const flattened = [];
    sections.forEach(section => {
      if (section.header.id !== 'default') {
        flattened.push(section.header);
      }
      // Flatten rowGroups to fields
      if (section.rowGroups) {
        section.rowGroups.forEach(row => {
          flattened.push(...(row.fields || []));
        });
      } else {
        flattened.push(...(section.fields || []));
      }
    });
    return flattened;
  };

  // Flatten sections back to flat array for saving
  const flattenSections = (sections) => {
    const flattened = [];
    sections.forEach(section => {
      if (section.header.id !== 'default') {
        flattened.push(section.header);
      }
      flattened.push(...section.fields);
    });
    return flattened;
  };

  // ============ ROW GROUP HELPERS ============
  
  // Migrate old section structure (fields[]) to new structure (rowGroups[])
  const migrateSectionToRowGroups = (section) => {
    // If section already has rowGroups, return as-is
    if (section.rowGroups && section.rowGroups.length > 0) {
      return section;
    }
    
    // Convert flat fields array to rowGroups based on width
    const fields = section.fields || [];
    if (fields.length === 0) {
      return {
        ...section,
        rowGroups: [{ id: `row_empty_${section.header?.id || 'default'}`, fields: [] }]
      };
    }
    
    // Helper to create stable row ID from fields
    const createRowId = (rowFields) => {
      if (rowFields.length === 0) return `row_empty_${section.header?.id || 'default'}`;
      return `row_${rowFields.map(f => f.id).join('_')}`;
    };
    
    // Group fields into rows based on cumulative width
    const rowGroups = [];
    let currentRowFields = [];
    let currentRowWidth = 0;
    
    fields.forEach((field, index) => {
      const fieldWidth = parseInt(field.width || '100');
      
      // Full-width fields always start a new row
      if (fieldWidth === 100) {
        // Save current row if it has fields
        if (currentRowFields.length > 0) {
          rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
        }
        // Add full-width field as its own row
        rowGroups.push({ id: createRowId([field]), fields: [field] });
        // Start new row
        currentRowFields = [];
        currentRowWidth = 0;
      } else if (currentRowWidth + fieldWidth > 100) {
        // Would exceed 100%, start new row
        if (currentRowFields.length > 0) {
          rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
        }
        currentRowFields = [field];
        currentRowWidth = fieldWidth;
      } else {
        // Add to current row
        currentRowFields.push(field);
        currentRowWidth += fieldWidth;
      }
    });
    
    // Don't forget the last row
    if (currentRowFields.length > 0) {
      rowGroups.push({ id: createRowId(currentRowFields), fields: currentRowFields });
    }
    
    return {
      ...section,
      rowGroups,
      fields: [] // Clear old fields array
    };
  };
  
  // Migrate all sections to use rowGroups
  const migrateSectionsToRowGroups = (sections) => {
    return sections.map(migrateSectionToRowGroups);
  };
  
  // Flatten rowGroups back to fields array (for backward compatibility when saving)
  const flattenRowGroupsToFields = (section) => {
    if (!section.rowGroups) return section.fields || [];
    return section.rowGroups.flatMap(row => row.fields || []);
  };
  
  // Convert rowGroups back to flat form_fields array for saving to DB
  const flattenRowGroupsToFormFields = (rowGroups) => {
    if (!rowGroups) return [];
    return rowGroups.flatMap(row => row.fields || []);
  };
  
  // Smart auto-split: Calculate optimal widths when fields are added to a row
  const calculateSmartWidths = (fieldCount) => {
    switch (fieldCount) {
      case 1: return ['100'];
      case 2: return ['50', '50'];
      case 3: return ['33', '33', '33'];
      case 4: return ['25', '25', '25', '25'];
      default: return Array(fieldCount).fill(Math.floor(100 / fieldCount).toString());
    }
  };
  
  // Apply smart auto-split to a row's fields
  const applySmartSplitToRow = (rowFields) => {
    if (!rowFields || rowFields.length === 0) return rowFields;
    const widths = calculateSmartWidths(rowFields.length);
    return rowFields.map((field, index) => ({
      ...field,
      width: widths[index] || '100'
    }));
  };
  
  // Add a field to a specific row with smart auto-split
  const addFieldToRow = (newField, sectionIndex, rowIndex) => {
    // Get current sections with row groups
    const sectionsWithRows = groupFieldsIntoSectionsAndRows(activeFormFields);
    
    if (!sectionsWithRows[sectionIndex] || !sectionsWithRows[sectionIndex].rowGroups[rowIndex]) {
      // Fallback: just add to activeFormFields
      setActiveFormFields(prev => [...prev, { ...newField, width: '100' }]);
      return;
    }
    
    // Get the target row
    const targetRow = sectionsWithRows[sectionIndex].rowGroups[rowIndex];
    const newRowFields = [...targetRow.fields, newField];
    
    // Apply smart split
    const updatedFields = applySmartSplitToRow(newRowFields);
    
    // Rebuild the flat array with updated widths
    const newActiveFields = [];
    sectionsWithRows.forEach((section, sIdx) => {
      // Add section header if not default
      if (section.header.id !== 'default') {
        newActiveFields.push(section.header);
      }
      // Add fields from each row
      section.rowGroups.forEach((row, rIdx) => {
        if (sIdx === sectionIndex && rIdx === rowIndex) {
          // This is the target row - use updated fields
          newActiveFields.push(...updatedFields);
        } else {
          newActiveFields.push(...row.fields);
        }
      });
    });
    
    setActiveFormFields(newActiveFields);
    toast.success(`Added field to row (auto-adjusted widths to ${calculateSmartWidths(newRowFields.length).join('/')}%)`);
  };
  
  // Add a new empty row after a specific row
  const addNewRowAfter = (sectionIndex, rowIndex) => {
    // This doesn't need to modify data structure - it's handled by adding a 100% field
    // The migration function will put it in its own row
    toast.info('Add a field to create a new row');
  };
  
  // Get all fields from sections with rowGroups (flattened)
  const getAllFieldsFromSections = (sections) => {
    return sections.flatMap(section => flattenRowGroupsToFields(section));
  };
  
  // Add a new row group to a section
  const addRowGroupToSection = (sectionId) => {
    setActiveFormSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const newRowGroup = { id: `row_${Date.now()}`, fields: [] };
        return {
          ...section,
          rowGroups: [...(section.rowGroups || []), newRowGroup]
        };
      }
      return section;
    }));
  };
  
  // Remove a row group from a section
  const removeRowGroup = (sectionId, rowGroupId) => {
    setActiveFormSections(prev => prev.map(section => {
      if (section.id === sectionId) {
        const newRowGroups = (section.rowGroups || []).filter(row => row.id !== rowGroupId);
        // Ensure at least one row group exists
        if (newRowGroups.length === 0) {
          newRowGroups.push({ id: `row_${Date.now()}`, fields: [] });
        }
        return { ...section, rowGroups: newRowGroups };
      }
      return section;
    }));
  };
  
  // Move a field to a different row group
  const moveFieldToRowGroup = (fieldId, fromSectionId, fromRowId, toSectionId, toRowId) => {
    setActiveFormSections(prev => {
      let fieldToMove = null;
      
      // First pass: find and remove the field
      const sectionsWithoutField = prev.map(section => {
        if (section.id === fromSectionId) {
          return {
            ...section,
            rowGroups: (section.rowGroups || []).map(row => {
              if (row.id === fromRowId) {
                const field = row.fields.find(f => f.id === fieldId);
                if (field) fieldToMove = field;
                return {
                  ...row,
                  fields: row.fields.filter(f => f.id !== fieldId)
                };
              }
              return row;
            })
          };
        }
        return section;
      });
      
      if (!fieldToMove) return prev;
      
      // Second pass: add field to target row
      return sectionsWithoutField.map(section => {
        if (section.id === toSectionId) {
          return {
            ...section,
            rowGroups: (section.rowGroups || []).map(row => {
              if (row.id === toRowId) {
                return {
                  ...row,
                  fields: [...row.fields, fieldToMove]
                };
              }
              return row;
            })
          };
        }
        return section;
      });
    });
  };
  
  // Render fields in a row group with proper grid layout
  const renderRowGroupFields = (rowGroup, renderFieldFn, isEditable = false) => {
    if (!rowGroup || !rowGroup.fields || rowGroup.fields.length === 0) {
      return null;
    }
    
    // Calculate grid columns based on field widths
    // Using CSS Grid with 12-column system for precise alignment
    const getGridColSpan = (width) => {
      const w = parseInt(width || '100');
      if (w >= 100) return 12;
      if (w >= 75) return 9;   // 75% = 9 columns
      if (w >= 66) return 8;   // 66% = 8 columns
      if (w >= 50) return 6;
      if (w >= 33) return 4;
      if (w >= 25) return 3;
      return Math.round(w / 100 * 12) || 12;
    };
    
    return (
      <div 
        key={rowGroup.id} 
        className="grid grid-cols-12 gap-3 mb-3"
        data-row-id={rowGroup.id}
      >
        {rowGroup.fields.map((field, index) => {
          const colSpan = getGridColSpan(field.width);
          return (
            <div 
              key={field.id} 
              className={`col-span-12 md:col-span-${colSpan}`}
              style={{ gridColumn: `span ${colSpan} / span ${colSpan}` }}
            >
              {renderFieldFn(field, index, rowGroup.fields)}
            </div>
          );
        })}
      </div>
    );
  };
  
  // ============ END ROW GROUP HELPERS ============

  // Handle dragging entire sections
  const handleSectionDragEnd = (event) => {
    const { active, over } = event;
    
    if (active.id !== over.id) {
      const sections = groupFieldsBySections(activeFormFields);
      const oldIndex = sections.findIndex((section) => section.header.id === active.id);
      const newIndex = sections.findIndex((section) => section.header.id === over.id);
      
      if (oldIndex !== -1 && newIndex !== -1) {
        const reorderedSections = arrayMove(sections, oldIndex, newIndex);
        setActiveFormFields(flattenSections(reorderedSections));
      }
    }
  };

  const addFormField = (fieldType, sectionId = null) => {
    // Base field structure
    const newField = {
      id: `field_${Date.now()}`,
      type: fieldType.type,
      // Leave label blank for user-editable fields so users must name them
      // Layout/special fields keep their type label as a default
      label: ['heading', 'static_text', 'divider', 'spacer', 'client_visibility_divider'].includes(fieldType.type) 
        ? fieldType.label 
        : '',
      required: false,
      // Layout properties
      width: '100', // percentage: 100, 50, 33, 25, or custom
      indentLevel: 0, // 0-3 for left padding
      backgroundColor: '',
      borderColor: '',
      textColor: '',
    };
    
    // Type-specific properties
    if (fieldType.type === 'text' || fieldType.type === 'textarea' || fieldType.type === 'email' || fieldType.type === 'phone') {
      newField.placeholder = 'Enter your response here...';
      newField.default_value = ''; // Default text value
    }
    
    if (fieldType.type === 'select' || fieldType.type === 'checkbox_group') {
      newField.options = ['Option 1', 'Option 2', 'Option 3'];
      newField.default_value = ''; // Default selected option
    }
    
    if (fieldType.type === 'multiselect') {
      newField.default_value = []; // Default selected options array
    }
    
    if (fieldType.type === 'service_dropdown') {
      newField.default_value = []; // Default selected services array (multi-select)
    }
    
    if (fieldType.type === 'medication_plan') {
      newField.label = 'Medication Plan';
      newField.required = false;
    }
    
    if (fieldType.type === 'set_a_reminder') {
      newField.id = `reminder_field_${newField.id || Date.now()}`;
      newField.label = 'Follow-Up Reminder';
      newField.required = false;
      newField.config = {
        offset_anchor: 'date_of_service',
        output_types: ['notification'],
        draft_config: { target_template_id: '', target_template_name: '' },
        recipients: { include_rendering_provider: true, additional_user_ids: [] },
        allowed_offsets: ['1m', '3m', '6m', '9m', '1y'],
        allow_custom: true,
      };
    }
    
    if (fieldType.type === 'radio') {
      newField.options = ['Yes', 'No']; // Better default for radio buttons
      newField.default_value = ''; // Default selected radio option
    }
    
    if (fieldType.type === 'checkbox') {
      newField.default_value = false; // Default checkbox state
    }
    
    if (fieldType.type === 'date') {
      newField.default_value = ''; // Default date value (can be 'today' or specific date)
    }
    
    // Organization data dropdowns - populate with live data
    if (fieldType.type === 'group_dropdown') {
      // Note: groups come from clientGroups - filter to only active ones (check both is_active and status)
      newField.options = (clientGroups || []).filter(g => g.status === 'active' || g.is_active === true || (g.is_active !== false && !g.status)).map(g => g.name);
      newField.default_value = '';
    }
    
    if (fieldType.type === 'heading') {
      newField.label = 'Section Heading';
      newField.headingLevel = 'h2'; // h1, h2, h3
      newField.textAlign = 'left'; // left, center, right
    }
    
    if (fieldType.type === 'static_text') {
      newField.label = 'Instructions or information';
      newField.content = 'Enter your text here. This text cannot be edited by form users.';
      newField.isBold = false;
      newField.isItalic = false;
      newField.borderEnabled = false;
      newField.borderThickness = 'thin';
      newField.borderColor = '#d1d5db';
      newField.imageAlign = 'left';
    }
    
    if (fieldType.type === 'divider') {
      newField.label = 'Divider';
      newField.thickness = 1; // pixels
      newField.color = '#cccccc';
    }
    
    if (fieldType.type === 'spacer') {
      newField.label = 'Spacer';
      newField.height = 20; // pixels
    }
    
    if (fieldType.type === 'container') {
      newField.label = 'Shaded Box';
      newField.containerFields = []; // Fields inside this container
      newField.padding = 16;
      newField.borderRadius = 8;
      newField.borderWidth = 1;
    }
    
    if (fieldType.type === 'row_placeholder') {
      newField.label = 'Empty Row';
      newField.width = '100'; // Always full width
      newField.isPlaceholder = true; // Mark as placeholder (hidden in preview/view)
    }
    
    // Client Data Fields (read-only, auto-populated)
    if (fieldType.category === 'client_data') {
      // client_diagnoses_selector is interactive, not auto-populated
      if (fieldType.type === 'client_diagnoses_selector') {
        newField.isReadOnly = false;
        newField.autoPopulate = false;
        newField.label = 'Client Diagnoses (Selectable)';
        newField.showColumns = ['code', 'description', 'date'];
      } else if (fieldType.type === 'add_contacts') {
        // Interactive button field — adds chart contacts from the note.
        newField.isReadOnly = false;
        newField.autoPopulate = false;
        newField.label = 'Contacts Added This Session';
      } else {
        newField.isReadOnly = true;
        newField.autoPopulate = true;
      }
      
      if (fieldType.type === 'client_diagnoses') {
        newField.label = 'Client Diagnoses';
        newField.showColumns = ['code', 'description', 'tag', 'date'];
      }
      
      if (fieldType.type === 'client_medications') {
        newField.label = 'Current Medications';
        newField.showColumns = ['name', 'dosage', 'frequency', 'prescriber', 'purpose'];
      }
      
      if (fieldType.type === 'client_insurance') {
        newField.label = 'Insurance Information';
        newField.showColumns = ['type', 'company', 'member_id', 'group'];
      }
      
      if (fieldType.type === 'client_providers') {
        newField.label = 'Assigned Providers';
        newField.showColumns = ['name', 'role', 'specialty'];
      }
    }
    
    // Add to activeFormFields
    if (sectionId && sectionId !== 'default') {
      // Insert after the last field in the specified section
      setActiveFormFields(fields => {
        const newFields = [...fields];
        
        // Find the section header index
        const sectionIndex = newFields.findIndex(f => f.id === sectionId);
        if (sectionIndex === -1) {
          // Section not found, append to end
          return [...newFields, newField];
        }
        
        // Find the next section header or end of array
        let insertIndex = newFields.length;
        for (let i = sectionIndex + 1; i < newFields.length; i++) {
          if (newFields[i].type === 'section_header') {
            insertIndex = i;
            break;
          }
        }
        
        // Insert before the next section (or at end)
        newFields.splice(insertIndex, 0, newField);
        return newFields;
      });
    } else if (sectionId === 'default') {
      // Default section (no section headers) - find the first section_header or append to end
      setActiveFormFields(fields => {
        const newFields = [...fields];
        const firstSectionIndex = newFields.findIndex(f => f.type === 'section_header');
        if (firstSectionIndex === -1) {
          // No sections at all, append to end
          return [...newFields, newField];
        } else {
          // Insert before the first section header
          newFields.splice(firstSectionIndex, 0, newField);
          return newFields;
        }
      });
    } else {
      // No section specified, append to end
      setActiveFormFields(fields => [...fields, newField]);
    }
    toast.success(`Added "${fieldType.label}" field`);
  };

  const removeFormField = (fieldId) => {
    const fieldToRemove = activeFormFields.find(f => f.id === fieldId);
    setActiveFormFields(fields => fields.filter(field => field.id !== fieldId));
    if (fieldToRemove) {
      toast.success(`Removed "${fieldToRemove.label}" field`);
    }
  };
  
  const moveFieldToSection = (fieldId, targetSectionId) => {
    const sections = groupFieldsBySections(activeFormFields);
    
    // Find the field and remove it from current section
    let fieldToMove = null;
    let newSections = sections.map(section => {
      const fieldInSection = section.fields.find(f => f.id === fieldId);
      if (fieldInSection) {
        fieldToMove = fieldInSection;
        return {
          ...section,
          fields: section.fields.filter(f => f.id !== fieldId)
        };
      }
      return section;
    });
    
    if (!fieldToMove) return;
    
    // Add field to target section
    newSections = newSections.map(section => {
      if (section.header.id === targetSectionId) {
        return {
          ...section,
          fields: [...section.fields, fieldToMove]
        };
      }
      return section;
    });
    
    // Update activeFormFields
    setActiveFormFields(flattenSections(newSections));
  };
  
  const duplicateFormField = (fieldId) => {
    const fieldToDuplicate = activeFormFields.find(field => field.id === fieldId);
    if (fieldToDuplicate) {
      const duplicatedField = {
        ...fieldToDuplicate,
        id: `field_${Date.now()}`,
        label: fieldToDuplicate.label  // Keep the same label - each instance is its own thing
      };
      
      const fieldIndex = activeFormFields.findIndex(field => field.id === fieldId);
      const newFields = [...activeFormFields];
      newFields.splice(fieldIndex + 1, 0, duplicatedField);
      setActiveFormFields(newFields);
      
      toast.success("Field duplicated successfully");
    }
  };

  const updateFormField = useCallback((fieldId, updates) => {
    setActiveFormFields(fields => fields.map(field => 
      field.id === fieldId ? { ...field, ...updates } : field
    ));
  }, []);

  // Auto-sync static text content from cache to state every 5 seconds while form builder is open
  // This prevents content loss when React re-renders the component
  useEffect(() => {
    if (!open) return;
    
    const syncStaticTextToState = () => {
      const cache = staticTextEditorCache.current;
      let hasChanges = false;
      
      // Check if any cached items have user edits before triggering a state update
      cache.forEach((cached) => {
        if (cached.hasUserEdits) hasChanges = true;
      });
      
      if (!hasChanges) return;
      
      // Batch all field updates into a single setActiveFormFields call
      setActiveFormFields(fields => {
        let changed = false;
        const updated = fields.map(field => {
          if (field.type === 'static_text') {
            const cached = cache.get(field.id);
            if (cached && cached.hasUserEdits && cached.content !== field.content) {
              changed = true;
              return { ...field, content: cached.content };
            }
          }
          return field;
        });
        return changed ? updated : fields;
      });
    };
    
    // Sync every 5 seconds
    const intervalId = setInterval(syncStaticTextToState, 5000);
    
    // Also sync when user switches away from the tab
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'hidden') {
        syncStaticTextToState();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    return () => {
      clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [open]);

  const updateSection = (sectionId, updates) => {
    setActiveFormSections(sections => sections.map(section => 
      section.id === sectionId ? { ...section, ...updates } : section
    ));
  };

  // ── Eligibility client-chart state (data lives in useClientEligibility
  //    hook; only the UI-dialog open/close booleans + 270/271 preview
  //    state stay here since they're chart-screen UI concerns).

  const handleCreateFormTemplate = async (e) => {
    e.preventDefault();
    try {
      // Calculate max_score if this is a scored assessment
      let scoringConfig = formTemplateData.scoring_config || {};
      if (formTemplateData.is_scored_assessment) {
        let maxScore = 0;
        activeFormFields.forEach(field => {
          if (field.is_scored && field.score_values) {
            const fieldMax = Math.max(...Object.values(field.score_values));
            maxScore += fieldMax;
          }
        });
        scoringConfig = { ...scoringConfig, max_score: maxScore };
      }
      
      const templateData = {
        ...formTemplateData,
        form_fields: syncLegacyAssessmentKeys(activeFormFieldsRef.current),
        scoring_config: scoringConfig,
        status_notifications: (formTemplateData.status_notifications || []).filter(r => r.status && (r.user_ids || []).length > 0)
      };
      
      if (editingFormTemplate) {
        // Update existing form template
        await axios.put(`${API}/form-templates/${editingFormTemplate.id}`, templateData);
        // Optimistically update local state
        setFormTemplates(prev => prev.map(t => 
          t.id === editingFormTemplate.id ? { ...t, ...templateData } : t
        ));
        toast.success("Form template updated successfully");
        setEditingFormTemplate(null);
      } else {
        // Create new form template
        const response = await axios.post(`${API}/form-templates`, templateData);
        // Optimistically add to local state
        setFormTemplates(prev => [...prev, response.data]);
        toast.success("Form template created successfully");
      }
      
      handleOpenChange(false);
      setFormTemplateData({
        name: '',
        description: '',
        form_type: '',
        form_fields: [],
        billable: false,
        requires_provider_signature: true,
        requires_supervisor_signature: false,
        allow_client_signature: false,
        require_client_signature: false,
        is_scored_assessment: false,
        is_group_template: false,
        available_in_quick_create: true,
        allow_external_download: false,
        allow_print_for_signature: false,
        allow_send_to_client: false,
        scoring_config: { max_score: 0, severity_ranges: [], clinical_alerts: [] }
      });
      setActiveFormFields([]);
      // Refresh form templates specifically (not all data) - skip cache after save
      fetchFormTemplates(true);
      // Return to the Forms Management dialog after saving
      if (onCancelToManagement) onCancelToManagement();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save form template");
    }
  };


  const memoizedGroupedSections = useMemo(() => {
    return groupFieldsIntoSectionsAndRows(activeFormFields);
    
  }, [activeFormFields]);

  // Cmd/Ctrl+S — Save Progress (moved from the legacy global keydown handler)
  useEffect(() => {
    if (!open) return;
    const handler = (e) => {
      if ((e.ctrlKey || e.metaKey) && e.key === 's' && open) {
        const t = e.target;
        const isTextField = t && (
          t.tagName === 'INPUT' || t.tagName === 'TEXTAREA' || t.isContentEditable
        );
        if (isTextField) return;
        e.preventDefault();

        // Check if form builder is open
        if (open && activeFormFieldsRef.current.length > 0) {
          void 0 && console.log('✓ Cmd/Ctrl+S detected - saving form builder progress');
          
          // First, sync any static text from cache to state before saving
          const cache = staticTextEditorCache.current;
          const updatedFields = activeFormFieldsRef.current.map(field => {
            if (field.type === 'static_text') {
              const cached = cache.get(field.id);
              if (cached && cached.hasUserEdits && cached.content !== field.content) {
                return { ...field, content: cached.content };
              }
            }
            return field;
          });
          
          // Trigger save
          (async () => {
            try {
              const templateData = {
                ...formTemplateDataRef.current,
                form_fields: syncLegacyAssessmentKeys(updatedFields),
                status_notifications: (formTemplateDataRef.current.status_notifications || []).filter(r => r.status && (r.user_ids || []).length > 0)
              };
              
              if (editingFormTemplate) {
                await axios.put(`${API}/form-templates/${editingFormTemplate.id}`, templateData);
                // Update local state with synced content
                setActiveFormFields(updatedFields);
                // Clear user edits flags in cache after successful save
                cache.forEach((value, key) => {
                  cache.set(key, { ...value, hasUserEdits: false });
                });
                toast.success("✓ Saved! (⌘S)");
              } else if (formTemplateDataRef.current.name) {
                const response = await axios.post(`${API}/form-templates`, templateData);
                setEditingFormTemplate(response.data);
                setActiveFormFields(updatedFields);
                toast.success("✓ Template created and saved! (⌘S)");
              } else {
                toast.error("Please enter a template name first");
              }
              // Form templates don't need fetchData - they're static data
            } catch (error) {
              toast.error(error.response?.data?.detail || "Failed to save");
            }
          })();
        }
        return;
      }
      

    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
    
  }, [open, editingFormTemplate]);

  return (
    <>
                {/* Form Template Builder Dialog */}
                <Dialog open={open && !showFormPreview} onOpenChange={handleOpenChange}>
                      <DialogContent className="max-w-[98vw] w-[98vw] max-h-[95vh] p-0 flex flex-col">
                        {/* Field Type Context Menu - for right-click to pick section */}
                        {fieldContextMenu.show && (
                          <div 
                            className="fixed z-[100] bg-white border rounded-lg shadow-lg py-1 min-w-[200px]"
                            style={{ top: fieldContextMenu.y, left: fieldContextMenu.x }}
                            onClick={() => setFieldContextMenu({ show: false, x: 0, y: 0, fieldType: null })}
                          >
                            <div className="px-3 py-2 text-xs font-semibold text-gray-500 border-b">
                              Add "{fieldContextMenu.fieldType?.label}" to section:
                            </div>
                            {memoizedGroupedSections.map((section, idx) => (
                              <button
                                key={section.header?.id || idx}
                                className="w-full px-3 py-2 text-left text-sm hover:bg-blue-50 hover:text-blue-700 flex items-center gap-2"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  addFormField(fieldContextMenu.fieldType, section.header?.id);
                                  setFieldContextMenu({ show: false, x: 0, y: 0, fieldType: null });
                                }}
                              >
                                <span className="text-gray-400">§</span>
                                {section.header?.label || 'Default Section'}
                              </button>
                            ))}
                          </div>
                        )}
                        
                        {/* Click outside to close context menu */}
                        {fieldContextMenu.show && (
                          <div 
                            className="fixed inset-0 z-[99]" 
                            onClick={() => setFieldContextMenu({ show: false, x: 0, y: 0, fieldType: null })}
                          />
                        )}
                        
                        <div className="sticky top-0 bg-white z-10 border-b shadow-sm">
                          <DialogHeader className="p-6 pb-3 flex flex-row items-start justify-between">
                            <DialogTitle>{editingFormTemplate ? 'Form Builder - Edit Template' : 'Form Builder - Create Template'}</DialogTitle>
                            {headerExtras || null}
                          </DialogHeader>
                          
                          {/* Collapsible Header Content */}
                          <div className="px-6 pb-4">
                            <button
                              type="button"
                              onClick={() => setFormHeaderCollapsed(!formHeaderCollapsed)}
                              className="w-full flex items-center justify-between text-sm font-medium text-gray-700 hover:text-gray-900 mb-2"
                            >
                              <span>Form Settings {formHeaderCollapsed ? '(Click to expand)' : ''}</span>
                              <ChevronDown className={`h-4 w-4 transition-transform ${formHeaderCollapsed ? 'rotate-180' : ''}`} />
                            </button>
                            
                            {!formHeaderCollapsed && (
                              <div className="space-y-3 max-h-[40vh] overflow-y-auto pr-2">
                                <div className="grid grid-cols-2 gap-3">
                                  <div>
                                    <Label htmlFor="form_name" className="text-xs">Template Name *</Label>
                                    <DebouncedInput
                                      id="form_name"
                                      value={formTemplateData.name}
                                      onChange={(e) => setFormTemplateData(prev => ({...prev, name: e.target.value}))}
                                      required
                                      className="h-8 text-sm"
                                      placeholder="e.g., Client Intake Form"
                                    />
                                  </div>
                                  <div>
                                    <Label htmlFor="form_type" className="text-xs">Form Type</Label>
                                    <Select
                                      value={formTemplateData.form_type}
                                      onValueChange={(value) => setFormTemplateData(prev => ({...prev, form_type: value}))}
                                    >
                                      <SelectTrigger className="h-8 text-sm">
                                        <SelectValue placeholder="Select form type" />
                                      </SelectTrigger>
                                      <SelectContent>
                                        {[...formTypes].sort((a, b) => (a.sort_order ?? 999) - (b.sort_order ?? 999) || a.name.localeCompare(b.name)).map(type => (
                                          <SelectItem key={type.id} value={type.name}>
                                            {type.name}
                                          </SelectItem>
                                        ))}
                                      </SelectContent>
                                    </Select>
                                  </div>
                                </div>
                                
                                <div>
                                  <Label htmlFor="form_description" className="text-xs">Description</Label>
                                  <DebouncedTextarea
                                    id="form_description"
                                    value={formTemplateData.description}
                                    onChange={(e) => setFormTemplateData(prev => ({...prev, description: e.target.value}))}
                                    className="h-16 text-sm"
                                    placeholder="Describe what this form is used for..."
                                  />
                                </div>
                                
                                {/* Status Notifications (Jun 2026) */}
                                <div className="p-3 border border-amber-200 rounded-lg bg-amber-50/60" data-testid="status-notifications-section">
                                  <div className="flex items-start justify-between gap-2 mb-1">
                                    <div>
                                      <span className="text-sm font-medium">Status Notifications</span>
                                      <p className="text-xs text-gray-600">Automatically notify selected users when a document using this template reaches a status (e.g. Supervisor Signed, Ready for Billing).</p>
                                    </div>
                                    <Button
                                      type="button" size="sm" variant="outline" className="h-7 text-xs shrink-0"
                                      data-testid="add-status-notification-rule-btn"
                                      onClick={() => setFormTemplateData(prev => ({
                                        ...prev,
                                        status_notifications: [...(prev.status_notifications || []), { status: '', user_ids: [] }],
                                      }))}
                                    >
                                      <Plus className="h-3 w-3 mr-1" /> Add Rule
                                    </Button>
                                  </div>
                                  {(formTemplateData.status_notifications || []).length === 0 && (
                                    <p className="text-xs text-gray-400 italic" data-testid="status-notifications-empty">No notification rules configured.</p>
                                  )}
                                  {(formTemplateData.status_notifications || []).map((rule, idx) => (
                                    <div key={idx} className="flex items-center gap-2 mt-2" data-testid={`status-notification-rule-${idx}`}>
                                      <Select value={rule.status || ''} onValueChange={(v) => updateStatusNotificationRule(idx, { status: v })}>
                                        <SelectTrigger className="h-8 text-xs w-60 bg-white" data-testid={`status-notification-status-select-${idx}`}>
                                          <SelectValue placeholder="Select status trigger" />
                                        </SelectTrigger>
                                        <SelectContent>
                                          {STATUS_NOTIFICATION_OPTIONS.map(o => (
                                            <SelectItem key={o.value} value={o.value} className="text-xs">{o.label}</SelectItem>
                                          ))}
                                        </SelectContent>
                                      </Select>
                                      <Popover>
                                        <PopoverTrigger asChild>
                                          <Button type="button" variant="outline" className="h-8 text-xs flex-1 justify-between font-normal bg-white" data-testid={`status-notification-users-btn-${idx}`}>
                                            <span className="truncate">
                                              {(rule.user_ids || []).length > 0
                                                ? (activeStaff.filter(u => (rule.user_ids || []).includes(u.id)).map(u => u.full_name || u.email).join(', ') || `${rule.user_ids.length} user(s)`)
                                                : 'Select users to notify'}
                                            </span>
                                            <ChevronDown className="h-3 w-3 ml-1 shrink-0" />
                                          </Button>
                                        </PopoverTrigger>
                                        <PopoverContent className="w-72 p-2 max-h-64 overflow-y-auto" align="start">
                                          {activeStaff.length === 0 && <p className="text-xs text-gray-400 italic p-1">No staff users found.</p>}
                                          {activeStaff.map(u => (
                                            <label key={u.id} className="flex items-center gap-2 py-1 px-1 text-xs hover:bg-gray-50 rounded cursor-pointer">
                                              <input
                                                type="checkbox" className="h-3 w-3"
                                                data-testid={`status-notification-user-checkbox-${idx}-${u.id}`}
                                                checked={(rule.user_ids || []).includes(u.id)}
                                                onChange={(e) => {
                                                  const ids = new Set(rule.user_ids || []);
                                                  if (e.target.checked) ids.add(u.id); else ids.delete(u.id);
                                                  updateStatusNotificationRule(idx, { user_ids: [...ids] });
                                                }}
                                              />
                                              <span>{u.full_name || u.email}{u.role ? ` (${u.role})` : ''}</span>
                                            </label>
                                          ))}
                                        </PopoverContent>
                                      </Popover>
                                      <Button
                                        type="button" variant="ghost" size="sm" className="h-8 w-8 p-0 text-red-500 hover:text-red-700"
                                        data-testid={`status-notification-remove-${idx}`}
                                        onClick={() => setFormTemplateData(prev => ({
                                          ...prev,
                                          status_notifications: (prev.status_notifications || []).filter((_, i) => i !== idx),
                                        }))}
                                      >
                                        <X className="h-3 w-3" />
                                      </Button>
                                    </div>
                                  ))}
                                </div>

                                {/* Billable and Signature Settings - Compact */}
                                <div className="grid grid-cols-3 gap-2 p-2 bg-gray-50 rounded text-xs">
                                  <label className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.billable || false}
                                      onChange={(e) => setFormTemplateData(prev => ({...prev, billable: e.target.checked}))}
                                      className="h-3 w-3"
                                    />
                                    <span>Billable</span>
                                  </label>
                                  <label className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.requires_provider_signature !== false}
                                      onChange={(e) => setFormTemplateData(prev => ({...prev, requires_provider_signature: e.target.checked}))}
                                      className="h-3 w-3"
                                    />
                                    <span>Provider Sig</span>
                                  </label>
                                  <label className="flex items-center space-x-1" title="When enabled, notes using this template will REQUIRE supervisor signature before completion">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.requires_supervisor_signature || false}
                                      onChange={(e) => setFormTemplateData(prev => ({...prev, requires_supervisor_signature: e.target.checked}))}
                                      className="h-3 w-3"
                                    />
                                    <span>Supervisor Sig Required</span>
                                  </label>
                                  <label className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.allow_client_signature || false}
                                      onChange={(e) => setFormTemplateData(prev => ({...prev, allow_client_signature: e.target.checked}))}
                                      className="h-3 w-3"
                                    />
                                    <span>Allow Client Sig</span>
                                  </label>
                                  <label className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.require_client_signature || false}
                                      onChange={(e) => setFormTemplateData(prev => ({...prev, require_client_signature: e.target.checked}))}
                                      disabled={!formTemplateData.allow_client_signature}
                                      className="h-3 w-3 disabled:opacity-50"
                                    />
                                    <span>Require Client Sig</span>
                                  </label>
                                  <label className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.allow_external_download || false}
                                      onChange={(e) => setFormTemplateData(prev => ({...prev, allow_external_download: e.target.checked}))}
                                      className="h-3 w-3"
                                    />
                                    <span>Allow Download</span>
                                  </label>
                                  {formTemplateData.allow_external_download && (
                                    <label className="flex items-center space-x-1 ml-4">
                                      <input
                                        type="checkbox"
                                        checked={formTemplateData.download_requires_signature !== false}
                                        onChange={(e) => setFormTemplateData(prev => ({...prev, download_requires_signature: e.target.checked}))}
                                        className="h-3 w-3"
                                      />
                                      <span className="text-gray-600">Requires signature to download</span>
                                    </label>
                                  )}
                                  {/* Feb 2026 — `allow_print_for_signature` is no
                                      longer a gating flag.  Print for Signature
                                      is now universally available in the note
                                      Options dropdown for every authenticated
                                      user (provider, office, supervisor, admin).
                                      The DB field is retained for backwards
                                      compatibility with existing template
                                      documents but the checkbox is hidden so
                                      admins aren't presented with a setting
                                      that no longer does anything. */}
                                  <label className="flex items-center space-x-1">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.allow_send_to_client || false}
                                      onChange={(e) => setFormTemplateData(prev => ({...prev, allow_send_to_client: e.target.checked}))}
                                      className="h-3 w-3"
                                    />
                                    <span>Allow Send to Client</span>
                                  </label>
                                </div>
                                
                                {/* Scored Assessment Toggle */}
                                <div className="mt-3 p-3 border rounded-lg bg-blue-50">
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.is_scored_assessment || false}
                                      onChange={(e) => setFormTemplateData(prev => ({
                                        ...prev, 
                                        is_scored_assessment: e.target.checked,
                                        scoring_config: e.target.checked ? {
                                          max_score: 0,
                                          severity_ranges: [
                                            { label: 'Minimal', min: 0, max: 4, color: '#22c55e' },
                                            { label: 'Mild', min: 5, max: 9, color: '#eab308' },
                                            { label: 'Moderate', min: 10, max: 14, color: '#f97316' },
                                            { label: 'Severe', min: 15, max: 99, color: '#ef4444' }
                                          ],
                                          clinical_alerts: []
                                        } : { max_score: 0, severity_ranges: [], clinical_alerts: [] }
                                      }))}
                                      className="h-4 w-4"
                                    />
                                    <div>
                                      <span className="font-medium text-sm">Scored Assessment</span>
                                      <p className="text-xs text-gray-600">Enable to track scores and progress over time (e.g., PHQ-9, GAD-7)</p>
                                    </div>
                                  </label>
                                  
                                  {/* Scoring Configuration Panel */}
                                  {formTemplateData.is_scored_assessment && (
                                    <div className="mt-3 space-y-3 pt-3 border-t border-blue-200">
                                      <div>
                                        <Label className="text-xs font-semibold">Severity Ranges</Label>
                                        <p className="text-xs text-gray-500 mb-2">Define score ranges and their severity labels</p>
                                        <div className="space-y-2">
                                          {(formTemplateData.scoring_config?.severity_ranges || []).map((range, idx) => (
                                            <div key={idx} className="flex items-center gap-2 bg-white p-2 rounded border">
                                              <DebouncedInput
                                                value={range.label}
                                                onChange={(e) => {
                                                  const newRanges = [...(formTemplateData.scoring_config?.severity_ranges || [])];
                                                  newRanges[idx] = { ...newRanges[idx], label: e.target.value };
                                                  setFormTemplateData(prev => ({
                                                    ...prev,
                                                    scoring_config: { ...prev.scoring_config, severity_ranges: newRanges }
                                                  }));
                                                }}
                                                placeholder="Label"
                                                className="h-7 text-xs w-24"
                                              />
                                              <span className="text-xs text-gray-500">Score:</span>
                                              <Input
                                                type="number"
                                                value={range.min}
                                                onChange={(e) => {
                                                  const newRanges = [...(formTemplateData.scoring_config?.severity_ranges || [])];
                                                  newRanges[idx] = { ...newRanges[idx], min: parseInt(e.target.value) || 0 };
                                                  setFormTemplateData(prev => ({
                                                    ...prev,
                                                    scoring_config: { ...prev.scoring_config, severity_ranges: newRanges }
                                                  }));
                                                }}
                                                className="h-7 text-xs w-14"
                                              />
                                              <span className="text-xs">-</span>
                                              <Input
                                                type="number"
                                                value={range.max}
                                                onChange={(e) => {
                                                  const newRanges = [...(formTemplateData.scoring_config?.severity_ranges || [])];
                                                  newRanges[idx] = { ...newRanges[idx], max: parseInt(e.target.value) || 0 };
                                                  setFormTemplateData(prev => ({
                                                    ...prev,
                                                    scoring_config: { ...prev.scoring_config, severity_ranges: newRanges }
                                                  }));
                                                }}
                                                className="h-7 text-xs w-14"
                                              />
                                              <input
                                                type="color"
                                                value={range.color || '#6b7280'}
                                                onChange={(e) => {
                                                  const newRanges = [...(formTemplateData.scoring_config?.severity_ranges || [])];
                                                  newRanges[idx] = { ...newRanges[idx], color: e.target.value };
                                                  setFormTemplateData(prev => ({
                                                    ...prev,
                                                    scoring_config: { ...prev.scoring_config, severity_ranges: newRanges }
                                                  }));
                                                }}
                                                className="h-7 w-7 rounded cursor-pointer"
                                              />
                                              <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                onClick={() => {
                                                  const newRanges = (formTemplateData.scoring_config?.severity_ranges || []).filter((_, i) => i !== idx);
                                                  setFormTemplateData(prev => ({
                                                    ...prev,
                                                    scoring_config: { ...prev.scoring_config, severity_ranges: newRanges }
                                                  }));
                                                }}
                                                className="h-7 w-7 p-0 text-red-500 hover:text-red-700"
                                              >
                                                <X className="h-3 w-3" />
                                              </Button>
                                            </div>
                                          ))}
                                          <Button
                                            type="button"
                                            variant="outline"
                                            size="sm"
                                            onClick={() => {
                                              const ranges = formTemplateData.scoring_config?.severity_ranges || [];
                                              const lastMax = ranges.length > 0 ? ranges[ranges.length - 1].max + 1 : 0;
                                              setFormTemplateData(prev => ({
                                                ...prev,
                                                scoring_config: {
                                                  ...prev.scoring_config,
                                                  severity_ranges: [...ranges, { label: 'New Level', min: lastMax, max: lastMax + 4, color: '#6b7280' }]
                                                }
                                              }));
                                            }}
                                            className="h-7 text-xs"
                                          >
                                            <Plus className="h-3 w-3 mr-1" /> Add Severity Level
                                          </Button>
                                        </div>
                                      </div>
                                      
                                      <div className="text-xs text-blue-700 bg-blue-100 p-2 rounded">
                                        <strong>Tip:</strong> Mark individual fields as "Scored" in the field settings below to assign point values to each answer option.
                                      </div>
                                    </div>
                                  )}
                                </div>
                                
                                {/* Group Template Toggle */}
                                <div className="mt-3 p-3 border rounded-lg bg-purple-50">
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.is_group_template || false}
                                      onChange={(e) => setFormTemplateData(prev => ({
                                        ...prev, 
                                        is_group_template: e.target.checked
                                      }))}
                                      className="h-4 w-4"
                                    />
                                    <div>
                                      <span className="font-medium text-sm text-purple-800">Group Note Template</span>
                                      <p className="text-xs text-purple-600">Enable for group therapy sessions. Fields can be marked as shared (same for all) or per-member (unique for each client).</p>
                                    </div>
                                  </label>
                                </div>
                                
                                {/* Discharge Form Toggle */}
                                <div className="mt-3 p-3 border rounded-lg bg-red-50">
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.is_discharge_form || false}
                                      onChange={(e) => setFormTemplateData(prev => ({
                                        ...prev, 
                                        is_discharge_form: e.target.checked
                                      }))}
                                      className="h-4 w-4"
                                    />
                                    <div>
                                      <span className="font-medium text-sm text-red-800">Discharge Form</span>
                                      <p className="text-xs text-red-600">Mark this template as a discharge form. A signed note from this template is required before a client can be discharged from services that require discharge.</p>
                                    </div>
                                  </label>
                                </div>
                                
                                {/* Quick Create Visibility Toggle */}
                                <div className="mt-3 p-3 border rounded-lg bg-amber-50">
                                  <label className="flex items-center space-x-2">
                                    <input
                                      type="checkbox"
                                      checked={formTemplateData.available_in_quick_create !== false}
                                      onChange={(e) => setFormTemplateData(prev => ({
                                        ...prev, 
                                        available_in_quick_create: e.target.checked
                                      }))}
                                      className="h-4 w-4"
                                    />
                                    <div>
                                      <span className="font-medium text-sm text-amber-800">Show in Quick Create Menu</span>
                                      <p className="text-xs text-amber-600">When enabled, this template appears in the "Create Note from Template" dropdown. Disable for archived or internal-only templates.</p>
                                    </div>
                                  </label>
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <form onSubmit={handleCreateFormTemplate} className="flex-1 min-h-0 overflow-y-auto p-6 pt-2">
                          
                          {/* Form Builder Interface */}
                          <div className="flex items-center justify-between mb-2">
                            <Label className="text-sm font-semibold">Form Builder</Label>
                            <Button
                              type="button"
                              variant="outline"
                              size="sm"
                              onClick={() => { setPreviewMode('edit'); setShowFormPreview(true); }}
                              data-testid="form-builder-preview-btn"
                            >
                              <Eye className="h-4 w-4 mr-1" />
                              Preview
                            </Button>
                          </div>
                          
                          {true ? (
                            // EDIT MODE
                            <div className="flex gap-4 min-h-[600px]">
                              {/* Field Palette - Collapsible */}
                              <div className={`${isFieldPaletteCollapsed ? 'w-10' : 'w-48'} transition-all duration-200 flex-shrink-0`}>
                                <div className={`border rounded bg-gray-50 max-h-[700px] h-full ${isFieldPaletteCollapsed ? 'p-1 overflow-y-auto' : 'p-3 overflow-y-auto'}`}>
                                  {/* Collapse/Expand Toggle */}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className={`${isFieldPaletteCollapsed ? 'w-full p-1' : 'w-full mb-2'} hover:bg-gray-200`}
                                    onClick={() => setIsFieldPaletteCollapsed(!isFieldPaletteCollapsed)}
                                    title={isFieldPaletteCollapsed ? 'Expand field types' : 'Collapse field types'}
                                  >
                                    {isFieldPaletteCollapsed ? (
                                      <ChevronRight className="h-4 w-4" />
                                    ) : (
                                      <div className="flex items-center justify-between w-full">
                                        <span className="text-xs font-semibold">Field Types</span>
                                        <ChevronLeft className="h-4 w-4" />
                                      </div>
                                    )}
                                  </Button>
                                  
                                  {/* Collapsed: icon-only strip — click to add, full name on hover */}
                                  {isFieldPaletteCollapsed && (
                                    <div className="flex flex-col items-stretch gap-0.5 mt-1" data-testid="collapsed-field-palette">
                                      {['input', 'layout', 'client_data'].map((cat, ci) => (
                                        <React.Fragment key={cat}>
                                          {ci > 0 && <div className="border-t my-1" />}
                                          {availableFieldTypes.filter(f => f.category === cat).map((fieldType) => (
                                            <button
                                              key={fieldType.type}
                                              type="button"
                                              className="w-full h-7 flex items-center justify-center rounded hover:bg-teal-100 text-base leading-none"
                                              title={fieldType.label}
                                              data-testid={`collapsed-palette-${fieldType.type}`}
                                              onClick={() => addFormField(fieldType)}
                                              onContextMenu={(e) => {
                                                e.preventDefault();
                                                if (memoizedGroupedSections.length > 1) {
                                                  setFieldContextMenu({ show: true, x: e.clientX, y: e.clientY, fieldType });
                                                } else {
                                                  addFormField(fieldType);
                                                }
                                              }}
                                            >
                                              <span>{fieldType.icon}</span>
                                            </button>
                                          ))}
                                        </React.Fragment>
                                      ))}
                                    </div>
                                  )}
                                  
                                  {!isFieldPaletteCollapsed && (
                                    <>
                                <Label className="text-xs font-semibold mb-2 block">Input Fields</Label>
                                <div className="space-y-1 mb-3">
                                  {availableFieldTypes.filter(f => f.category === 'input').map((fieldType) => (
                                    <Button
                                      key={fieldType.type}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-xs py-1 h-auto"
                                      onClick={() => addFormField(fieldType)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (memoizedGroupedSections.length > 1) {
                                          setFieldContextMenu({ show: true, x: e.clientX, y: e.clientY, fieldType });
                                        } else {
                                          addFormField(fieldType);
                                        }
                                      }}
                                      type="button"
                                    >
                                      <span className="mr-1">{fieldType.icon}</span>
                                      {fieldType.label}
                                    </Button>
                                  ))}
                                </div>
                                
                                <Label className="text-xs font-semibold mb-2 block">Layout & Format</Label>
                                <div className="space-y-1 mb-3">
                                  {availableFieldTypes.filter(f => f.category === 'layout').map((fieldType) => (
                                    <Button
                                      key={fieldType.type}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-xs py-1 h-auto"
                                      onClick={() => addFormField(fieldType)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (memoizedGroupedSections.length > 1) {
                                          setFieldContextMenu({ show: true, x: e.clientX, y: e.clientY, fieldType });
                                        } else {
                                          addFormField(fieldType);
                                        }
                                      }}
                                      type="button"
                                    >
                                      <span className="mr-1">{fieldType.icon}</span>
                                      {fieldType.label}
                                    </Button>
                                  ))}
                                </div>
                                
                                <Label className="text-xs font-semibold mb-2 block">Client Data</Label>
                                <div className="space-y-1 mb-3">
                                  {availableFieldTypes.filter(f => f.category === 'client_data').map((fieldType) => (
                                    <Button
                                      key={fieldType.type}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-xs py-1 h-auto"
                                      onClick={() => addFormField(fieldType)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (memoizedGroupedSections.length > 1) {
                                          setFieldContextMenu({ show: true, x: e.clientX, y: e.clientY, fieldType });
                                        } else {
                                          addFormField(fieldType);
                                        }
                                      }}
                                      type="button"
                                    >
                                      <span className="mr-1">{fieldType.icon}</span>
                                      {fieldType.label}
                                    </Button>
                                  ))}
                                </div>
                                
                                <Label className="text-xs font-semibold mb-2 block">Organization Data</Label>
                                <div className="space-y-1 mb-3">
                                  {availableFieldTypes.filter(f => f.category === 'org_data').map((fieldType) => (
                                    <Button
                                      key={fieldType.type}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-xs py-1 h-auto"
                                      onClick={() => addFormField(fieldType)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (memoizedGroupedSections.length > 1) {
                                          setFieldContextMenu({ show: true, x: e.clientX, y: e.clientY, fieldType });
                                        } else {
                                          addFormField(fieldType);
                                        }
                                      }}
                                      type="button"
                                    >
                                      <span className="mr-1">{fieldType.icon}</span>
                                      {fieldType.label}
                                    </Button>
                                  ))}
                                </div>
                                
                                <Label className="text-xs font-semibold mb-2 block">Special</Label>
                                <div className="space-y-1 mb-3">
                                  {availableFieldTypes.filter(f => f.category === 'special').map((fieldType) => (
                                    <Button
                                      key={fieldType.type}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-xs py-1 h-auto"
                                      onClick={() => addFormField(fieldType)}
                                      onContextMenu={(e) => {
                                        e.preventDefault();
                                        if (memoizedGroupedSections.length > 1) {
                                          setFieldContextMenu({ show: true, x: e.clientX, y: e.clientY, fieldType });
                                        } else {
                                          addFormField(fieldType);
                                        }
                                      }}
                                      type="button"
                                    >
                                      <span className="mr-1">{fieldType.icon}</span>
                                      {fieldType.label}
                                    </Button>
                                  ))}
                                </div>
                                
                                <Label className="text-xs font-semibold mb-2 block text-purple-700">Clinical Components</Label>
                                <div className="space-y-1">
                                  {availableFieldTypes.filter(f => f.category === 'clinical').map((fieldType) => (
                                    <Button
                                      key={fieldType.type}
                                      variant="outline"
                                      size="sm"
                                      className="w-full justify-start text-xs py-1 h-auto border-purple-200 hover:bg-purple-50"
                                      data-testid={`palette-clinical-${fieldType.type}`}
                                      onClick={() => addFormField(fieldType)}
                                      type="button"
                                    >
                                      <span className="mr-1">{fieldType.icon}</span>
                                      {fieldType.label}
                                    </Button>
                                  ))}
                                </div>
                                    </>
                                  )}
                              </div>
                              </div>
                              
                              {/* Form Builder Canvas */}
                              <div className="flex-1 border rounded p-4 bg-white max-h-[700px] overflow-y-auto">
                                {activeFormFields.length === 0 ? (
                                  <div className="text-center text-gray-500 py-8">
                                    <p>No fields added yet.</p>
                                    <p className="text-sm">Click on field types to add them to your form.</p>
                                  </div>
                                ) : (
                                  <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleRowDragEnd} autoScroll={false}>
                                    <SortableContext items={[
                                      ...memoizedGroupedSections.map(s => s.header.id),
                                      ...memoizedGroupedSections.flatMap(s => 
                                        (s.rowGroups || []).map(r => r.id)
                                      ),
                                      ...activeFormFields.map(f => f.id)
                                    ]} strategy={verticalListSortingStrategy}>
                                      <div className="space-y-4">
                                        {memoizedGroupedSections.map((section, sectionIndex) => (
                                          <SortableSection
                                            key={section.header.id}
                                            section={section}
                                            availableFieldTypes={availableFieldTypes}
                                            updateFormField={updateFormField}
                                            removeFormField={removeFormField}
                                            duplicateFormField={duplicateFormField}
                                            moveFieldToSection={moveFieldToSection}
                                            allSections={memoizedGroupedSections}
                                          >
                                            <div className="space-y-3">
                                              {(section.rowGroups || []).map((rowGroup, rowIndex) => (
                                                <DroppableRow
                                                  key={rowGroup.id}
                                                  rowId={rowGroup.id}
                                                  rowIndex={rowIndex}
                                                  isEmpty={rowGroup.fields.length === 0}
                                                >
                                                  {/* Row container with fields and add button */}
                                                  <div className="flex items-start gap-2">
                                                    {/* Fields in this row - using CSS Grid */}
                                                    <div className="grid grid-cols-12 gap-2 flex-1 min-h-[40px]">
                                                    {rowGroup.fields.length === 0 ? (
                                                      <div className="col-span-12 flex items-center justify-center text-gray-400 text-xs py-2 border border-dashed rounded">
                                                        Drop fields here
                                                      </div>
                                                    ) : (
                                                      rowGroup.fields.map((field, fieldIndex) => {
                                                        // Special handling for row_placeholder - render as more compact
                                                        if (field.type === 'row_placeholder') {
                                                          return (
                                                            <div 
                                                              key={field.id}
                                                              className="col-span-12"
                                                            >
                                                              <div className="border border-dashed border-gray-300 rounded px-3 py-1.5 bg-gray-50/50 flex items-center justify-between">
                                                                <span className="text-xs text-gray-400">⊡ Empty row</span>
                                                                <Button
                                                                  type="button"
                                                                  size="sm"
                                                                  variant="ghost"
                                                                  onClick={() => removeFormField(field.id)}
                                                                  className="text-red-400 hover:text-red-600 h-5 w-5 p-0"
                                                                >
                                                                  <X className="h-3 w-3" />
                                                                </Button>
                                                              </div>
                                                            </div>
                                                          );
                                                        }
                                                        
                                                        const colSpan = (() => {
                                                          const w = parseInt(field.width || '100');
                                                          // Handle grid column format (1-12) vs percentage format (25-100)
                                                          // If width is 12 or less, treat as column span directly
                                                          if (w <= 12) return w;
                                                          // Otherwise treat as percentage
                                                          if (w >= 100) return 12;
                                                          if (w >= 75) return 9;   // 75% = 9 columns
                                                          if (w >= 66) return 8;   // 66% = 8 columns
                                                          if (w >= 50) return 6;
                                                          if (w >= 33) return 4;
                                                          if (w >= 25) return 3;
                                                          return Math.max(1, Math.round(w / 100 * 12));
                                                        })();
                                                        
                                                        return (
                                                          <div 
                                                            key={field.id}
                                                            style={{ gridColumn: `span ${colSpan} / span ${colSpan}` }}
                                                          >
                                                            <SortableFieldItem
                                                              key={field.id}
                                                              field={field}
                                                              availableFieldTypes={availableFieldTypes}
                                                              updateFormField={updateFormField}
                                                              removeFormField={removeFormField}
                                                              duplicateFormField={duplicateFormField}
                                                              moveFieldToSection={moveFieldToSection}
                                                              allSections={memoizedGroupedSections}
                                                              currentSectionId={section.header.id}
                                                            >
                                                            {/* Layout Properties */}
                                                            <div className="grid grid-cols-3 gap-2 mb-2">
                                                              <div>
                                                                <Label className="text-xs">Width</Label>
                                                            <Select 
                                                              value={field.width || '100'} 
                                                              onValueChange={(value) => updateFormField(field.id, { width: value })}
                                                            >
                                                              <SelectTrigger className="h-8 text-xs">
                                                                <SelectValue />
                                                              </SelectTrigger>
                                                              <SelectContent>
                                                                <SelectItem value="100">Full (100%)</SelectItem>
                                                                <SelectItem value="75">Three-quarters (75%)</SelectItem>
                                                                <SelectItem value="66">Two-thirds (66%)</SelectItem>
                                                                <SelectItem value="50">Half (50%)</SelectItem>
                                                                <SelectItem value="33">Third (33%)</SelectItem>
                                                                <SelectItem value="25">Quarter (25%)</SelectItem>
                                                              </SelectContent>
                                                            </Select>
                                                          </div>
                                                          
                                                          <div>
                                                            <Label className="text-xs">Indent</Label>
                                                            <Select 
                                                              value={(field.indentLevel || 0).toString()} 
                                                              onValueChange={(value) => updateFormField(field.id, { indentLevel: parseInt(value) })}
                                                            >
                                                              <SelectTrigger className="h-8 text-xs">
                                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="0">None</SelectItem>
                                                <SelectItem value="1">Level 1</SelectItem>
                                                <SelectItem value="2">Level 2</SelectItem>
                                                <SelectItem value="3">Level 3</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                          
                                          <div>
                                            <Label className="text-xs">BG Color</Label>
                                            <Input
                                              type="color"
                                              value={field.backgroundColor || '#ffffff'}
                                              onChange={(e) => updateFormField(field.id, { backgroundColor: e.target.value })}
                                              className="h-8 p-1"
                                            />
                                          </div>
                                          <div>
                                            <Label className="text-xs">Font Color</Label>
                                            <Input
                                              type="color"
                                              value={field.fontColor || '#000000'}
                                              onChange={(e) => updateFormField(field.id, { fontColor: e.target.value })}
                                              className="h-8 p-1"
                                            />
                                          </div>
                                        </div>
                                        
                                        {/* create_task config (Feb 2026) — surfaced to the
                                            template designer so each create_task field can
                                            specify its own assignee default, due offset,
                                            title template, and description label. */}
                                        {field.type === 'create_task' && (
                                          <div className="space-y-2 border-l-4 border-purple-400 bg-purple-50 rounded p-3">
                                            <div className="text-xs font-semibold text-purple-800 uppercase">
                                              Task Generation Settings
                                            </div>
                                            {/* Task type (Jun 2026) — picks the routing target +
                                                due-date policy from the admin-managed type list. */}
                                            <div>
                                              <div className="flex items-center justify-between">
                                                <Label className="text-xs">Task type</Label>
                                                <button
                                                  type="button"
                                                  className="text-[10px] text-purple-700 underline"
                                                  onClick={() => setShowTaskTypeManager(true)}
                                                  data-testid={`field-${field.id}-manage-task-types`}
                                                >
                                                  Manage task types…
                                                </button>
                                              </div>
                                              <select
                                                value={field.task_type_key || 'reschedule'}
                                                onChange={(e) => {
                                                  const t = taskTypes.find((x) => x.key === e.target.value);
                                                  updateFormField(field.id, {
                                                    task_type_key: t?.key || 'reschedule',
                                                    task_type_label: t?.label || 'Reschedule Request',
                                                    action_type: (t?.key || 'reschedule') === 'reschedule' ? 'reschedule_appointment' : 'general',
                                                    due_date_required: t ? t.due_date_required !== false : true,
                                                    task_category: t?.task_category || field.task_category,
                                                  });
                                                }}
                                                className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
                                                data-testid={`field-${field.id}-task-type-select`}
                                              >
                                                {taskTypes.map((t) => (
                                                  <option key={t.key} value={t.key}>
                                                    {t.label}{t.due_date_required === false ? ' (due date optional)' : ''}
                                                  </option>
                                                ))}
                                              </select>
                                              <p className="text-[10px] text-slate-500 mt-1">
                                                Tasks route to the type's target (unless overridden below).
                                                Reschedule-specific options only apply to the Reschedule type.
                                              </p>
                                            </div>
                                            <div>
                                              <Label className="text-xs">Default task title</Label>
                                              <Input
                                                type="text"
                                                placeholder="e.g. Reschedule – {client_name}"
                                                value={field.task_title_template || ''}
                                                onChange={(e) => updateFormField(field.id, { task_title_template: e.target.value })}
                                                data-testid={`field-${field.id}-task-title-template`}
                                              />
                                              <p className="text-[10px] text-slate-500 mt-1">
                                                This pre-fills the task name when the note is opened — the provider can still edit it.
                                                Use <span className="font-mono">{'{client_name}'}</span> to inject the client's name.
                                              </p>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              <div>
                                                <Label className="text-xs">Default assignee</Label>
                                                <select
                                                  value={field.default_assignee_type || 'task_type_route'}
                                                  onChange={(e) => updateFormField(field.id, { default_assignee_type: e.target.value })}
                                                  className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
                                                  data-testid={`field-${field.id}-default-assignee-type`}
                                                >
                                                  <option value="task_type_route">Task type's routing target</option>
                                                  <option value="provider_scheduler">This provider's scheduler</option>
                                                  <option value="provider_biller">This provider's assigned biller</option>
                                                  <option value="specific_user">Specific user…</option>
                                                  <option value="scheduler">First scheduler (any)</option>
                                                  <option value="biller">First biller</option>
                                                  <option value="billing_manager">First billing manager</option>
                                                  <option value="supervisor">My supervisor</option>
                                                  <option value="supervisee">My supervisee</option>
                                                  <option value="current_user">Me (the provider)</option>
                                                  <option value="pick_at_runtime">No default — pick when filling out</option>
                                                </select>
                                                {field.default_assignee_type === 'specific_user' && (
                                                  <select
                                                    value={field.specific_user_id || ''}
                                                    onChange={(e) => updateFormField(field.id, { specific_user_id: e.target.value })}
                                                    className="block w-full h-9 mt-1 rounded border border-slate-300 bg-white px-2 text-sm"
                                                    data-testid={`field-${field.id}-specific-user`}
                                                  >
                                                    <option value="">— choose a user —</option>
                                                    {(users || [])
                                                      .filter(u => !u.is_archived)
                                                      .sort((a, b) => (a.full_name || '').localeCompare(b.full_name || ''))
                                                      .map(u => (
                                                        <option key={u.id} value={u.id}>{u.full_name || u.email}</option>
                                                      ))}
                                                  </select>
                                                )}
                                                <p className="text-[10px] text-slate-500 mt-1">
                                                  <span className="font-medium">This provider's scheduler</span> /
                                                  <span className="font-medium"> assigned biller</span> look up the
                                                  scheduler (Schedule&nbsp;For mapping) or biller (Assigned&nbsp;Billers)
                                                  linked to the note's provider. Falls back to the first
                                                  scheduler/biller if no one is dedicated.
                                                </p>
                                              </div>
                                              <div>
                                                <Label className="text-xs">Default due in</Label>
                                                {/* Feb 2026 — providers often think in weeks
                                                    ("re-check in 2 weeks") rather than days.  We
                                                    store the canonical value as days
                                                    (`default_due_offset_days`) so the runtime widget
                                                    stays simple; the toggle here just converts on the
                                                    way in/out.  A value already divisible by 7 shows
                                                    as weeks by default. */}
                                                {(() => {
                                                  const days = Number.isFinite(parseInt(field.default_due_offset_days, 10))
                                                    ? parseInt(field.default_due_offset_days, 10)
                                                    : 14;
                                                  const unit = field.default_due_offset_unit
                                                    || (days % 7 === 0 && days >= 7 ? 'weeks' : 'days');
                                                  const displayValue = unit === 'weeks' ? Math.round(days / 7) : days;
                                                  return (
                                                    <div className="flex items-stretch gap-2">
                                                      <Input
                                                        type="number"
                                                        min={0}
                                                        max={unit === 'weeks' ? 52 : 365}
                                                        value={displayValue}
                                                        onChange={(e) => {
                                                          const v = parseInt(e.target.value, 10);
                                                          const safe = Number.isFinite(v) && v >= 0 ? v : 0;
                                                          const canonical = unit === 'weeks' ? safe * 7 : safe;
                                                          updateFormField(field.id, {
                                                            default_due_offset_days: canonical,
                                                            default_due_offset_unit: unit,
                                                          });
                                                        }}
                                                        data-testid={`field-${field.id}-default-due-offset`}
                                                        className="flex-1"
                                                      />
                                                      <select
                                                        value={unit}
                                                        onChange={(e) => {
                                                          // Switching unit preserves the canonical
                                                          // days value when going days→weeks (rounds),
                                                          // and recomputes days when going weeks→days.
                                                          const nextUnit = e.target.value;
                                                          let nextDays = days;
                                                          if (nextUnit === 'weeks' && unit === 'days') {
                                                            // Round to nearest whole week so the input
                                                            // doesn't show a fractional number.
                                                            nextDays = Math.round(days / 7) * 7;
                                                          }
                                                          updateFormField(field.id, {
                                                            default_due_offset_days: nextDays,
                                                            default_due_offset_unit: nextUnit,
                                                          });
                                                        }}
                                                        className="h-9 rounded border border-slate-300 bg-white px-2 text-sm"
                                                        data-testid={`field-${field.id}-default-due-offset-unit`}
                                                      >
                                                        <option value="days">days</option>
                                                        <option value="weeks">weeks</option>
                                                      </select>
                                                    </div>
                                                  );
                                                })()}
                                              </div>
                                            </div>
                                            <div className="grid grid-cols-2 gap-2">
                                              <div>
                                                <Label className="text-xs">Task category</Label>
                                                <select
                                                  value={field.task_category || 'scheduling'}
                                                  onChange={(e) => updateFormField(field.id, { task_category: e.target.value })}
                                                  className="block w-full h-9 rounded border border-slate-300 bg-white px-2 text-sm"
                                                  data-testid={`field-${field.id}-task-category`}
                                                >
                                                  <option value="scheduling">Scheduling</option>
                                                  <option value="billing">Billing</option>
                                                  <option value="clinical">Clinical</option>
                                                  <option value="administrative">Administrative</option>
                                                  <option value="other">Other</option>
                                                </select>
                                              </div>
                                              <div>
                                                <Label className="text-xs">Details label</Label>
                                                <Input
                                                  type="text"
                                                  placeholder="Details"
                                                  value={field.description_label || ''}
                                                  onChange={(e) => updateFormField(field.id, { description_label: e.target.value })}
                                                  data-testid={`field-${field.id}-description-label`}
                                                />
                                              </div>
                                            </div>
                                            {/* Reschedule timing options (Mar 2026) — per-template
                                                checklist of the choices the provider sees in the
                                                "When should the client be rescheduled?" dropdown.
                                                Jun 2026 — only shown for the Reschedule task type. */}
                                            {(field.action_type || 'reschedule_appointment') === 'reschedule_appointment' && (
                                            <div>
                                              <div className="flex items-center justify-between">
                                                <Label className="text-xs">Reschedule timing options</Label>
                                                <span className="text-[10px] text-slate-500">
                                                  Untick to hide from the provider's picker
                                                </span>
                                              </div>
                                              {(() => {
                                                const defaults = [
                                                  { key: 'same_week',  label: 'Same week (urgent)' },
                                                  { key: '1_week',     label: '1 week' },
                                                  { key: '2_weeks',    label: '2 weeks' },
                                                  { key: '3_weeks',    label: '3 weeks' },
                                                  { key: '1_month',    label: '1 month' },
                                                  { key: '6_weeks',    label: '6 weeks' },
                                                  { key: '2_months',   label: '2 months' },
                                                  { key: '3_months',   label: '3 months' },
                                                  { key: '6_months',   label: '6 months' },
                                                  { key: 'prn',        label: 'PRN / As needed' },
                                                  { key: 'discharge',  label: 'Discharge (no reschedule)' },
                                                  { key: 'other',      label: 'Other' },
                                                ];
                                                const stored = Array.isArray(field.reschedule_options) && field.reschedule_options.length > 0
                                                  ? field.reschedule_options
                                                  : null;
                                                const enabledKeys = new Set(
                                                  stored ? stored.map((o) => o.key) : defaults.map((o) => o.key)
                                                );
                                                const dayMap = {
                                                  same_week: 5, '1_week': 7, '2_weeks': 14, '3_weeks': 21,
                                                  '1_month': 30, '6_weeks': 42, '2_months': 60,
                                                  '3_months': 90, '6_months': 180,
                                                  prn: null, discharge: null, other: null,
                                                };
                                                const toggle = (key) => {
                                                  const nextEnabled = new Set(enabledKeys);
                                                  if (nextEnabled.has(key)) nextEnabled.delete(key);
                                                  else nextEnabled.add(key);
                                                  const nextList = defaults
                                                    .filter((o) => nextEnabled.has(o.key))
                                                    .map((o) => ({ key: o.key, label: o.label, days: dayMap[o.key] }));
                                                  updateFormField(field.id, { reschedule_options: nextList });
                                                };
                                                return (
                                                  <div className="grid grid-cols-2 gap-x-2 gap-y-1 mt-1 border rounded p-2 bg-white">
                                                    {defaults.map((o) => (
                                                      <label key={o.key} className="flex items-center gap-2 text-xs cursor-pointer">
                                                        <input
                                                          type="checkbox"
                                                          checked={enabledKeys.has(o.key)}
                                                          onChange={() => toggle(o.key)}
                                                          data-testid={`field-${field.id}-reschedule-opt-${o.key}`}
                                                        />
                                                        <span>{o.label}</span>
                                                      </label>
                                                    ))}
                                                  </div>
                                                );
                                              })()}
                                              <p className="text-[10px] text-slate-500 mt-1">
                                                Empty list = use all defaults.  The provider picks one
                                                at note-time and the scheduler sees the structured
                                                choice on the Task card.
                                              </p>
                                            </div>
                                            )}
                                          </div>
                                        )}

                                        {/* Field-specific options - Combined Options & Default Selection */}
                                        {(field.type === 'select' || field.type === 'radio' || field.type === 'multiselect' || field.type === 'checkbox_group') && (
                                          <div className="space-y-2">
                                            <div className="flex items-center justify-between">
                                              <Label className="text-xs">Options:</Label>
                                              <span className="text-xs text-gray-500">
                                                {field.type === 'checkbox_group' || field.type === 'multiselect' 
                                                  ? '☑️ = default checked' 
                                                  : '⭐ = default selected'}
                                              </span>
                                            </div>
                                            <div className="border rounded-md divide-y max-h-48 overflow-y-auto">
                                              {(field.options || []).map((opt, idx) => {
                                                const optLabel = getOptionLabel(opt);
                                                const optValue = typeof opt === 'object' ? (opt.value || opt.label) : opt;
                                                const isDefault = field.type === 'checkbox_group' || field.type === 'multiselect'
                                                  ? (field.default_value || []).includes(optValue)
                                                  : field.default_value === optValue;
                                                return (
                                                  <div key={idx} className="flex items-center gap-2 p-2 hover:bg-gray-50">
                                                    {/* Default toggle */}
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        if (field.type === 'checkbox_group' || field.type === 'multiselect') {
                                                          const currentDefaults = field.default_value || [];
                                                          const newDefaults = isDefault
                                                            ? currentDefaults.filter(v => v !== optValue)
                                                            : [...currentDefaults, optValue];
                                                          updateFormField(field.id, { default_value: newDefaults });
                                                        } else {
                                                          updateFormField(field.id, { default_value: isDefault ? null : optValue });
                                                        }
                                                      }}
                                                      className={`flex-shrink-0 w-5 h-5 flex items-center justify-center rounded ${
                                                        isDefault 
                                                          ? 'bg-blue-100 text-blue-600' 
                                                          : 'bg-gray-100 text-gray-400 hover:bg-gray-200'
                                                      }`}
                                                      title={isDefault ? 'Remove as default' : 'Set as default'}
                                                    >
                                                      {field.type === 'checkbox_group' || field.type === 'multiselect' 
                                                        ? (isDefault ? '☑️' : '☐') 
                                                        : (isDefault ? '⭐' : '☆')}
                                                    </button>
                                                    {/* Option text - editable */}
                                                    <DebouncedInput
                                                      value={optLabel}
                                                      onChange={(e) => {
                                                        const newOptions = [...(field.options || [])];
                                                        newOptions[idx] = e.target.value;
                                                        // Also update default_value if this option was selected
                                                        const updates = { options: newOptions };
                                                        if (field.type === 'checkbox_group' || field.type === 'multiselect') {
                                                          if ((field.default_value || []).includes(optValue)) {
                                                            updates.default_value = field.default_value.map(v => 
                                                              v === optValue ? e.target.value : v
                                                            );
                                                          }
                                                        } else if (field.default_value === optValue) {
                                                          updates.default_value = e.target.value;
                                                        }
                                                        updateFormField(field.id, updates);
                                                      }}
                                                      debounceMs={400}
                                                      className="flex-1 h-7 text-xs border-0 bg-transparent focus:ring-0 px-1"
                                                      placeholder={`Option ${idx + 1}`}
                                                    />
                                                    {/* Delete option */}
                                                    <button
                                                      type="button"
                                                      onClick={() => {
                                                        const newOptions = field.options.filter((_, i) => i !== idx);
                                                        const updates = { options: newOptions };
                                                        // Remove from defaults if deleted
                                                        if (field.type === 'checkbox_group' || field.type === 'multiselect') {
                                                          updates.default_value = (field.default_value || []).filter(v => v !== optValue);
                                                        } else if (field.default_value === optValue) {
                                                          updates.default_value = null;
                                                        }
                                                        updateFormField(field.id, updates);
                                                      }}
                                                      className="flex-shrink-0 text-red-400 hover:text-red-600 p-1"
                                                      title="Remove option"
                                                    >
                                                      <X className="h-3 w-3" />
                                                    </button>
                                                  </div>
                                                );
                                              })}
                                              {/* Add new option */}
                                              <div className="p-2">
                                                <button
                                                  type="button"
                                                  onClick={() => {
                                                    const newOptions = [...(field.options || []), `Option ${(field.options?.length || 0) + 1}`];
                                                    updateFormField(field.id, { options: newOptions });
                                                  }}
                                                  className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                                                >
                                                  <Plus className="h-3 w-3" />
                                                  Add option
                                                </button>
                                              </div>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Scoring Configuration for scored assessments */}
                                        {formTemplateData.is_scored_assessment && (field.type === 'select' || field.type === 'radio') && (
                                          <div className="border-t pt-3 mt-3 bg-blue-50 p-3 rounded-lg">
                                            <label className="flex items-center space-x-2 mb-2">
                                              <input
                                                type="checkbox"
                                                checked={field.is_scored || false}
                                                onChange={(e) => {
                                                  if (e.target.checked) {
                                                    // Initialize score_values with default scores (0, 1, 2, 3...)
                                                    const scoreValues = {};
                                                    (field.options || []).forEach((opt, idx) => {
                                                      scoreValues[opt] = idx;
                                                    });
                                                    updateFormField(field.id, { is_scored: true, score_values: scoreValues });
                                                  } else {
                                                    updateFormField(field.id, { is_scored: false, score_values: {} });
                                                  }
                                                }}
                                                className="h-4 w-4"
                                              />
                                              <div>
                                                <span className="font-medium text-xs text-blue-800">Score this field</span>
                                                <p className="text-xs text-blue-600">Include in assessment score calculation</p>
                                              </div>
                                            </label>
                                            
                                            {field.is_scored && field.options && field.options.length > 0 && (
                                              <div className="mt-2 space-y-1">
                                                <Label className="text-xs text-blue-800">Point values for each option:</Label>
                                                {field.options.map((option, optIdx) => {
                                                  const optLabel = getOptionLabel(option);
                                                  const optValue = typeof option === 'object' ? (option.value || option.label) : option;
                                                  return (
                                                  <div key={optIdx} className="flex items-center gap-2 bg-white p-2 rounded">
                                                    <span className="text-xs flex-1 truncate">{optLabel}</span>
                                                    <Input
                                                      type="number"
                                                      value={field.score_values?.[optValue] ?? (typeof option === 'object' ? option.score : optIdx) ?? optIdx}
                                                      onChange={(e) => {
                                                        const newScoreValues = { ...(field.score_values || {}) };
                                                        newScoreValues[optValue] = parseInt(e.target.value) || 0;
                                                        updateFormField(field.id, { score_values: newScoreValues });
                                                      }}
                                                      className="h-7 w-16 text-xs text-center"
                                                      min="0"
                                                    />
                                                    <span className="text-xs text-gray-500">pts</span>
                                                  </div>
                                                  );
                                                })}
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        
                                        {/* Default value configuration */}
                                        {(field.type === 'text' || field.type === 'email' || field.type === 'phone') && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs font-semibold">Default Value</Label>
                                            <p className="text-xs text-gray-500 mb-2">Pre-fill this field with a default value</p>
                                            <DebouncedInput
                                              value={field.default_value || ''}
                                              onChange={(e) => updateFormField(field.id, { default_value: e.target.value })}
                                              placeholder="Enter default value..."
                                              className="h-8 text-xs w-full"
                                            />
                                          </div>
                                        )}
                                        
                                        {/* Default value for textarea - larger, supports multi-line */}
                                        {field.type === 'textarea' && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs font-semibold">Default Value</Label>
                                            <p className="text-xs text-gray-500 mb-2">Pre-fill this field. Press Enter for new paragraphs.</p>
                                            <DebouncedTextarea
                                              value={field.default_value || ''}
                                              onChange={(e) => updateFormField(field.id, { default_value: e.target.value })}
                                              placeholder="Enter default value..."
                                              className="text-xs w-full min-h-[120px] resize-y"
                                              rows={6}
                                            />
                                          </div>
                                        )}
                                        
                                        {/* Textarea rows configuration */}
                                        {field.type === 'textarea' && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs font-semibold">Default Rows</Label>
                                            <p className="text-xs text-gray-500 mb-2">Set the initial height of the text area</p>
                                            <Select
                                              value={String(field.rows || '4')}
                                              onValueChange={(value) => updateFormField(field.id, { rows: parseInt(value) })}
                                            >
                                              <SelectTrigger className="h-8 text-xs w-32">
                                                <SelectValue placeholder="4 rows" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="1">1 row</SelectItem>
                                                <SelectItem value="2">2 rows</SelectItem>
                                                <SelectItem value="3">3 rows</SelectItem>
                                                <SelectItem value="4">4 rows</SelectItem>
                                                <SelectItem value="5">5 rows</SelectItem>
                                                <SelectItem value="6">6 rows</SelectItem>
                                                <SelectItem value="8">8 rows</SelectItem>
                                                <SelectItem value="10">10 rows</SelectItem>
                                                <SelectItem value="12">12 rows</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        
                                        {(field.type === 'select' || field.type === 'radio') && field.options && field.options.length > 0 && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs font-semibold">Default Selection</Label>
                                            <p className="text-xs text-gray-500 mb-2">Pre-select an option</p>
                                            <Select
                                              value={field.default_value || 'none'}
                                              onValueChange={(value) => updateFormField(field.id, { default_value: value === 'none' ? '' : value })}
                                            >
                                              <SelectTrigger className="h-8 text-xs">
                                                <SelectValue placeholder="No default selection" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">No default selection</SelectItem>
                                                {field.options.map((option, idx) => {
                                                  const optValue = typeof option === 'object' ? (option.value || option.label) : option;
                                                  const optLabel = getOptionLabel(option);
                                                  return (
                                                    <SelectItem key={idx} value={optValue}>{optLabel}</SelectItem>
                                                  );
                                                })}
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        
                                        {/* Radio multi-select option */}
                                        {field.type === 'radio' && (
                                          <div className="border-t pt-3 mt-3">
                                            <label className="flex items-center space-x-2">
                                              <input
                                                type="checkbox"
                                                checked={field.allowMultiple || false}
                                                onChange={(e) => updateFormField(field.id, { allowMultiple: e.target.checked })}
                                                className="h-4 w-4 rounded"
                                              />
                                              <div>
                                                <span className="text-xs font-semibold">Allow Multiple Selections</span>
                                                <p className="text-xs text-gray-500">Let users select more than one option</p>
                                              </div>
                                            </label>
                                          </div>
                                        )}
                                        
                                        {field.type === 'checkbox' && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs font-semibold">Default State</Label>
                                            <div className="flex items-center space-x-2 mt-2">
                                              <input
                                                type="checkbox"
                                                checked={field.default_value || false}
                                                onChange={(e) => updateFormField(field.id, { default_value: e.target.checked })}
                                                className="h-4 w-4"
                                              />
                                              <span className="text-xs">Checked by default</span>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {field.type === 'date' && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs font-semibold">Default Date</Label>
                                            <p className="text-xs text-gray-500 mb-2">Set a default date value</p>
                                            <Select
                                              value={field.default_value || 'none'}
                                              onValueChange={(value) => updateFormField(field.id, { default_value: value === 'none' ? '' : value })}
                                            >
                                              <SelectTrigger className="h-8 text-xs">
                                                <SelectValue placeholder="No default date" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">No default date</SelectItem>
                                                <SelectItem value="today">Today's date</SelectItem>
                                                <SelectItem value="custom">Custom date...</SelectItem>
                                              </SelectContent>
                                            </Select>
                                            {field.default_value === 'custom' && (
                                              <Input
                                                type="date"
                                                value={field.custom_default_date || ''}
                                                onChange={(e) => updateFormField(field.id, { custom_default_date: e.target.value })}
                                                className="mt-2 h-8 text-xs"
                                              />
                                            )}
                                          </div>
                                        )}
                                        
                                        {field.type === 'time' && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs font-semibold">Default Time</Label>
                                            <p className="text-xs text-gray-500 mb-2">Set a default time value</p>
                                            <Select
                                              value={field.default_value || 'none'}
                                              onValueChange={(value) => updateFormField(field.id, { default_value: value === 'none' ? '' : value })}
                                            >
                                              <SelectTrigger className="h-8 text-xs">
                                                <SelectValue placeholder="No default time" />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="none">No default time</SelectItem>
                                                <SelectItem value="now">Current time</SelectItem>
                                                <SelectItem value="custom">Custom time...</SelectItem>
                                              </SelectContent>
                                            </Select>
                                            {field.default_value === 'custom' && (
                                              <Input
                                                type="time"
                                                value={field.custom_default_time || ''}
                                                onChange={(e) => updateFormField(field.id, { custom_default_time: e.target.value })}
                                                className="mt-2 h-8 text-xs"
                                              />
                                            )}
                                          </div>
                                        )}
                                        
                                        {/* Default selections for multiselect */}
                                        {field.type === 'multiselect' && field.options && field.options.length > 0 && (
                                          <div className="border-t pt-3 mt-3">
                                            <Label className="text-xs font-semibold">Default Selections</Label>
                                            <p className="text-xs text-gray-500 mb-2">Pre-select options (click to toggle)</p>
                                            <div className="max-h-32 overflow-y-auto border rounded p-2 space-y-1">
                                              {field.options.map((option, idx) => {
                                                const optValue = typeof option === 'object' ? (option.value || option.label) : option;
                                                const optLabel = getOptionLabel(option);
                                                const isSelected = (field.default_value || []).includes(optValue);
                                                return (
                                                  <div key={idx} className="flex items-center gap-2">
                                                    <input
                                                      type="checkbox"
                                                      checked={isSelected}
                                                      onChange={(e) => {
                                                        const currentDefaults = field.default_value || [];
                                                        const newDefaults = e.target.checked
                                                          ? [...currentDefaults, optValue]
                                                          : currentDefaults.filter(v => v !== optValue);
                                                        updateFormField(field.id, { default_value: newDefaults });
                                                      }}
                                                      className="h-3 w-3"
                                                    />
                                                    <span className="text-xs">{optLabel}</span>
                                                  </div>
                                                );
                                              })}
                                            </div>
                                          </div>
                                        )}
                                        
                                        {/* Client Editable checkbox - for fields that clients can fill */}
                                        {!['static_text', 'heading', 'divider', 'spacer', 'section_header'].includes(field.type) && (
                                          <div className="border-t pt-3 mt-3">
                                            <div className="flex items-center space-x-2">
                                              <input
                                                type="checkbox"
                                                id={`client-editable-${field.id}`}
                                                checked={field.client_editable || false}
                                                onChange={(e) => updateFormField(field.id, { client_editable: e.target.checked })}
                                                className="h-4 w-4"
                                              />
                                              <Label htmlFor={`client-editable-${field.id}`} className="text-xs font-semibold cursor-pointer">
                                                Client Editable
                                              </Label>
                                            </div>
                                            <p className="text-xs text-gray-500 mt-1 ml-6">
                                              Allow clients to fill this field in the portal before signing
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Checkbox group column layout */}
                                        {field.type === 'checkbox_group' && (
                                          <div>
                                            <Label className="text-xs">Column Layout:</Label>
                                            <Select
                                              value={field.checkboxColumns || '1'}
                                              onValueChange={(value) => updateFormField(field.id, { checkboxColumns: value })}
                                            >
                                              <SelectTrigger className="h-8 text-xs mt-1">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="1">1 Column (Vertical List)</SelectItem>
                                                <SelectItem value="2">2 Columns</SelectItem>
                                                <SelectItem value="3">3 Columns</SelectItem>
                                              </SelectContent>
                                            </Select>
                                          </div>
                                        )}
                                        
                                        {/* Matrix field configuration */}
                                        {field.type === 'matrix' && (
                                          <div className="space-y-3">
                                            <div>
                                              <Label className="text-xs">Row Labels (one per line):</Label>
                                              <DebouncedTextarea
                                                value={field.rowLabels?.join('\n') || ''}
                                                onChange={(e) => updateFormField(field.id, { 
                                                  rowLabels: e.target.value.split('\n').filter(o => o.trim()) 
                                                })}
                                                className="mt-1"
                                                rows={3}
                                                placeholder="Row 1&#10;Row 2&#10;Row 3"
                                              />
                                            </div>
                                            <div className="flex items-center space-x-2">
                                              <input
                                                type="checkbox"
                                                id={`hide-row-labels-${field.id}`}
                                                checked={!!field.hide_row_labels}
                                                onChange={(e) => updateFormField(field.id, { hide_row_labels: e.target.checked })}
                                                className="h-4 w-4"
                                                data-testid={`hide-row-labels-toggle-${field.id}`}
                                              />
                                              <label htmlFor={`hide-row-labels-${field.id}`} className="text-xs font-medium cursor-pointer">
                                                Hide row labels
                                                <span className="text-gray-500 font-normal ml-1">(rows still work — label text is just not shown on the form)</span>
                                              </label>
                                            </div>
                                            {!!field.hide_row_labels && (
                                              <div className="flex items-center gap-2 ml-6">
                                                <Label className="text-xs">Starting rows:</Label>
                                                <Input
                                                  type="number"
                                                  min={0}
                                                  max={20}
                                                  className="h-8 w-20 text-xs"
                                                  value={field.initial_rows ?? (field.rowLabels?.length || 1)}
                                                  onChange={(e) => updateFormField(field.id, { initial_rows: parseInt(e.target.value, 10) || 0 })}
                                                  data-testid={`matrix-initial-rows-${field.id}`}
                                                />
                                                <span className="text-xs text-gray-500">blank rows the matrix starts with (no labels needed)</span>
                                              </div>
                                            )}
                                            <div>
                                              <Label className="text-xs">Column Headers (one per line):</Label>
                                              <DebouncedTextarea
                                                value={field.columnHeaders?.join('\n') || ''}
                                                onChange={(e) => updateFormField(field.id, { 
                                                  columnHeaders: e.target.value.split('\n').filter(o => o.trim()) 
                                                })}
                                                className="mt-1"
                                                rows={3}
                                                placeholder="Column 1&#10;Column 2&#10;Column 3"
                                              />
                                            </div>
                                            <div>
                                              <Label className="text-xs">Column Types:</Label>
                                              <p className="text-xs text-gray-500 mb-2">Configure each column's input type</p>
                                              <div className="space-y-2 max-h-48 overflow-y-auto border rounded p-2 bg-gray-50">
                                                {(field.columnHeaders || []).map((header, colIdx) => (
                                                  <div key={colIdx} className="flex items-center gap-2 bg-white p-2 rounded">
                                                    <span className="text-xs font-medium min-w-[100px]">{header}:</span>
                                                    <Select
                                                      value={field.columnTypes?.[colIdx] || 'text'}
                                                      onValueChange={(value) => {
                                                        const newColumnTypes = [...(field.columnTypes || [])];
                                                        newColumnTypes[colIdx] = value;
                                                        updateFormField(field.id, { columnTypes: newColumnTypes });
                                                      }}
                                                    >
                                                      <SelectTrigger className="h-7 text-xs flex-1">
                                                        <SelectValue />
                                                      </SelectTrigger>
                                                      <SelectContent>
                                                        <SelectItem value="text">Text Input</SelectItem>
                                                        <SelectItem value="dropdown">Dropdown</SelectItem>
                                                        <SelectItem value="checkbox">Checkbox</SelectItem>
                                                      </SelectContent>
                                                    </Select>
                                                    {(field.columnTypes?.[colIdx] || 'text') === 'dropdown' && (
                                                      <DebouncedInput
                                                        className="h-7 text-xs flex-1"
                                                        placeholder="Options (comma separated)"
                                                        value={field.columnDropdownOptions?.[colIdx] || ''}
                                                        onChange={(e) => {
                                                          const newOptions = [...(field.columnDropdownOptions || [])];
                                                          newOptions[colIdx] = e.target.value;
                                                          updateFormField(field.id, { columnDropdownOptions: newOptions });
                                                        }}
                                                      />
                                                    )}
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                            <div>
                                              <Label className="text-xs">Prefill Content (optional):</Label>
                                              <p className="text-xs text-gray-500 mb-2">Pre-populate cells per column — line 1 fills row 1, line 2 fills row 2, etc. Clinicians can edit or clear the prefilled text.</p>
                                              <div className="space-y-2 max-h-48 overflow-y-auto border rounded p-2 bg-gray-50">
                                                {(field.columnHeaders || []).map((header, colIdx) => (
                                                  <div key={colIdx} className="bg-white p-2 rounded">
                                                    <span className="text-xs font-medium block mb-1">{header}:</span>
                                                    <DebouncedTextarea
                                                      className="text-xs"
                                                      rows={3}
                                                      placeholder={'Row 1 prefill\nRow 2 prefill\n(leave a line blank for no prefill)'}
                                                      value={field.columnDefaults?.[colIdx] || ''}
                                                      onChange={(e) => {
                                                        const newDefaults = [...(field.columnDefaults || [])];
                                                        newDefaults[colIdx] = e.target.value;
                                                        updateFormField(field.id, { columnDefaults: newDefaults });
                                                      }}
                                                      data-testid={`matrix-prefill-${field.id}-${colIdx}`}
                                                    />
                                                  </div>
                                                ))}
                                              </div>
                                            </div>
                                            
                                            {/* Legacy cellType field - hidden but kept for backwards compatibility */}
                                            <div className="hidden">
                                              <Label className="text-xs">Cell Type (Legacy):</Label>
                                              <Select 
                                                value={field.cellType || 'text'} 
                                                onValueChange={(value) => updateFormField(field.id, { cellType: value })}
                                              >
                                                <SelectTrigger className="h-8 text-xs">
                                                  <SelectValue />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="text">Text Input</SelectItem>
                                                  <SelectItem value="dropdown">Dropdown</SelectItem>
                                                  <SelectItem value="checkbox">Checkbox</SelectItem>
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            {field.cellType === 'dropdown' && (
                                              <div>
                                                <Label className="text-xs">Dropdown Options (one per line):</Label>
                                                <DebouncedTextarea
                                                  value={field.dropdownOptions?.join('\n') || ''}
                                                  onChange={(e) => updateFormField(field.id, { 
                                                    dropdownOptions: e.target.value.split('\n').filter(o => o.trim()) 
                                                  })}
                                                  className="mt-1"
                                                  rows={3}
                                                  placeholder="Option A&#10;Option B&#10;Option C"
                                                />
                                              </div>
                                            )}
                                          </div>
                                        )}
                                        
                                        {/* Group Template Field Scope - Only show for group templates */}
                                        {formTemplateData.is_group_template && (
                                          <div className="border-t pt-3 mt-3 bg-purple-50 p-3 rounded">
                                            <Label className="text-xs font-semibold text-purple-800">Field Scope (Group Notes)</Label>
                                            <p className="text-xs text-purple-600 mb-2">Determine if this field is shared across all members or unique per member</p>
                                            <Select 
                                              value={field.scope || 'shared'} 
                                              onValueChange={(value) => updateFormField(field.id, { scope: value })}
                                            >
                                              <SelectTrigger className="h-8 text-xs">
                                                <SelectValue />
                                              </SelectTrigger>
                                              <SelectContent>
                                                <SelectItem value="shared">Shared (Same for all members)</SelectItem>
                                                <SelectItem value="per_member">Per Member (Unique for each client)</SelectItem>
                                              </SelectContent>
                                            </Select>
                                            <p className="text-xs text-gray-500 mt-1">
                                              {field.scope === 'per_member' 
                                                ? '📝 This field will appear separately for each group member' 
                                                : '📋 This field will be filled once and applied to all member notes'}
                                            </p>
                                          </div>
                                        )}
                                        
                                        {/* Conditional Logic */}
                                        <div className="border-t pt-3 mt-3">
                                          <Label className="text-xs font-semibold">Conditional Logic</Label>
                                          <p className="text-xs text-gray-500 mb-2">Show this field only when certain conditions are met</p>
                                          
                                          <div className="space-y-2">
                                            <div>
                                              <Label className="text-xs">Show if field:</Label>
                                              <Select 
                                                value={field.conditionalField || 'none'} 
                                                onValueChange={(value) => {
                                                  if (value === 'none') {
                                                    // Clear conditional logic
                                                    updateFormField(field.id, { 
                                                      conditionalField: null,
                                                      conditionalOperator: null,
                                                      conditionalValue: null
                                                    });
                                                  } else {
                                                    updateFormField(field.id, { conditionalField: value });
                                                  }
                                                }}
                                              >
                                                <SelectTrigger className="h-8 text-xs">
                                                  <SelectValue placeholder="Select a field..." />
                                                </SelectTrigger>
                                                <SelectContent>
                                                  <SelectItem value="none">None (Always show)</SelectItem>
                                                  {activeFormFields
                                                    .filter(f => f.id !== field.id && (f.type === 'select' || f.type === 'radio' || f.type === 'multiselect' || f.type === 'checkbox' || f.type === 'checkbox_group'))
                                                    .map(f => (
                                                      <SelectItem key={f.id} value={f.id}>
                                                        {f.label || f.type}
                                                      </SelectItem>
                                                    ))
                                                  }
                                                </SelectContent>
                                              </Select>
                                            </div>
                                            
                                            {field.conditionalField && (
                                              <>
                                                <div>
                                                  <Label className="text-xs">Condition:</Label>
                                                  <Select 
                                                    value={field.conditionalOperator || 'equals'} 
                                                    onValueChange={(value) => updateFormField(field.id, { conditionalOperator: value })}
                                                  >
                                                    <SelectTrigger className="h-8 text-xs">
                                                      <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="equals">Equals</SelectItem>
                                                      <SelectItem value="contains">Contains (for multi-select)</SelectItem>
                                                      <SelectItem value="not_equals">Not Equals</SelectItem>
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                                
                                                <div>
                                                  <Label className="text-xs">Value:</Label>
                                                  {(() => {
                                                    // Show dropdown of options if source field has predefined options
                                                    const sourceField = activeFormFields.find(f => f.id === field.conditionalField);
                                                    const sourceOptions = sourceField?.options;
                                                    if (sourceOptions && sourceOptions.length > 0) {
                                                      return (
                                                        <Select
                                                          value={field.conditionalValue || ''}
                                                          onValueChange={(value) => updateFormField(field.id, { conditionalValue: value })}
                                                        >
                                                          <SelectTrigger className="h-8 text-xs">
                                                            <SelectValue placeholder="Select expected value..." />
                                                          </SelectTrigger>
                                                          <SelectContent>
                                                            {sourceOptions.map((opt, i) => {
                                                              const val = typeof opt === 'object' ? (opt.value || opt.label) : opt;
                                                              const label = typeof opt === 'object' ? (opt.label || opt.value) : opt;
                                                              return <SelectItem key={i} value={val}>{label}</SelectItem>;
                                                            })}
                                                          </SelectContent>
                                                        </Select>
                                                      );
                                                    }
                                                    return (
                                                      <DebouncedInput
                                                        value={field.conditionalValue || ''}
                                                        onChange={(e) => updateFormField(field.id, { conditionalValue: e.target.value })}
                                                        placeholder="Enter expected value..."
                                                        className="h-8 text-xs"
                                                      />
                                                    );
                                                  })()}
                                                  <p className="text-xs text-gray-500 mt-1">
                                                    For multi-select, enter one of the selected values
                                                  </p>
                                                </div>
                                              </>
                                            )}
                                          </div>
                                        </div>
                                        
                                        {field.type === 'heading' && (
                                          <div className="space-y-3">
                                            {/* Heading Text Input */}
                                            <div>
                                              <Label className="text-xs">Heading Text</Label>
                                              <DebouncedInput
                                                value={field.label || ''}
                                                onChange={(e) => updateFormField(field.id, { label: e.target.value })}
                                                placeholder="Enter heading text..."
                                                className="h-8 text-sm"
                                              />
                                            </div>
                                            
                                            <div className="grid grid-cols-2 gap-2">
                                              <div>
                                                <Label className="text-xs">Heading Level</Label>
                                                <Select 
                                                  value={field.headingLevel || 'h2'} 
                                                  onValueChange={(value) => updateFormField(field.id, { headingLevel: value })}
                                                >
                                                  <SelectTrigger className="h-8 text-xs">
                                                    <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="h1">H1 (Largest)</SelectItem>
                                                    <SelectItem value="h2">H2 (Medium)</SelectItem>
                                                    <SelectItem value="h3">H3 (Small)</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                              <div>
                                                <Label className="text-xs">Align</Label>
                                                <Select 
                                                  value={field.textAlign || 'left'} 
                                                  onValueChange={(value) => updateFormField(field.id, { textAlign: value })}
                                                >
                                                  <SelectTrigger className="h-8 text-xs">
                                                    <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="left">Left</SelectItem>
                                                    <SelectItem value="center">Center</SelectItem>
                                                    <SelectItem value="right">Right</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            </div>
                                            
                                            <div>
                                              <Label className="text-xs">Font Color</Label>
                                              <div className="flex gap-2 items-center">
                                                <Input
                                                  type="color"
                                                  value={field.fontColor || '#000000'}
                                                  onChange={(e) => updateFormField(field.id, { fontColor: e.target.value })}
                                                  className="h-8 w-16"
                                                />
                                                <span className="text-xs text-gray-600">{field.fontColor || '#000000'}</span>
                                                {field.fontColor && field.fontColor !== '#000000' && (
                                                  <Button
                                                    type="button"
                                                    size="sm"
                                                    variant="ghost"
                                                    onClick={() => updateFormField(field.id, { fontColor: '#000000' })}
                                                    className="h-6 text-xs"
                                                  >
                                                    Reset
                                                  </Button>
                                                )}
                                              </div>
                                            </div>
                                            
                                            <div className="flex items-center space-x-4">
                                              <label className="flex items-center text-xs">
                                                <input
                                                  type="checkbox"
                                                  checked={field.isBold || false}
                                                  onChange={(e) => updateFormField(field.id, { isBold: e.target.checked })}
                                                  className="mr-1"
                                                />
                                                Bold
                                              </label>
                                              <label className="flex items-center text-xs">
                                                <input
                                                  type="checkbox"
                                                  checked={field.isItalic || false}
                                                  onChange={(e) => updateFormField(field.id, { isItalic: e.target.checked })}
                                                  className="mr-1"
                                                />
                                                Italic
                                              </label>
                                              <label className="flex items-center text-xs">
                                                <input
                                                  type="checkbox"
                                                  checked={field.isUnderline || false}
                                                  onChange={(e) => updateFormField(field.id, { isUnderline: e.target.checked })}
                                                  className="mr-1"
                                                />
                                                Underline
                                              </label>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {field.type === 'static_text' && (
                                          <div className="space-y-3">
                                            <div>
                                              <Label className="text-xs mb-1 block">Field Name (for reference):</Label>
                                              <DebouncedInput
                                                key={`static-label-${field.id}`}
                                                value={field.label || ''}
                                                onChange={(e) => updateFormField(field.id, { label: e.target.value })}
                                                placeholder="e.g., Consent Statement, Instructions..."
                                                className="h-8 text-xs"
                                              />
                                              <p className="text-xs text-gray-500 mt-1">
                                                This name is for your reference only - it won't be shown to users.
                                              </p>
                                            </div>
                                            <div>
                                              <Label className="text-xs mb-2 block">Content:</Label>
                                              
                                              {/* Rich Text Editor with inline formatting */}
                                              {/* Using cache ref to preserve content across re-renders */}
                                              <div 
                                                key={`static-editor-${field.id}`}
                                                className={`min-h-[100px] p-3 border rounded-md bg-white resize-y overflow-auto focus:outline-none focus:ring-2 focus:ring-blue-500 prose prose-sm max-w-none ${staticTextImageClass(field)}`}
                                                contentEditable
                                                suppressContentEditableWarning
                                                data-field-id={field.id}
                                                data-placeholder="Enter text content..."
                                                style={{ 
                                                  minHeight: '100px',
                                                  fontSize: `${field.fontSize || 14}px`,
                                                  ...staticTextBorderStyle(field)
                                                }}
                                                ref={(el) => {
                                                  if (el) {
                                                    const cache = staticTextEditorCache.current;
                                                    const cached = cache.get(field.id);
                                                    
                                                    // If we have cached content from user edits, use that
                                                    if (cached && cached.hasUserEdits) {
                                                      if (el.innerHTML !== cached.content) {
                                                        el.innerHTML = cached.content;
                                                      }
                                                    } else if (!cached || !cached.initialized) {
                                                      // First time init - use field.content
                                                      el.innerHTML = field.content || '';
                                                      cache.set(field.id, { 
                                                        content: field.content || '', 
                                                        initialized: true,
                                                        hasUserEdits: false 
                                                      });
                                                    }
                                                  }
                                                }}
                                                onFocus={(e) => {
                                                  const cache = staticTextEditorCache.current;
                                                  const cached = cache.get(field.id) || {};
                                                  cache.set(field.id, { ...cached, hasUserEdits: true });
                                                }}
                                                onInput={(e) => {
                                                  // Save to cache on every input to prevent loss
                                                  const cache = staticTextEditorCache.current;
                                                  cache.set(field.id, { 
                                                    content: e.target.innerHTML,
                                                    initialized: true,
                                                    hasUserEdits: true 
                                                  });
                                                }}
                                                onPaste={(e) => {
                                                  // Preserve formatting when pasting
                                                  e.preventDefault();
                                                  
                                                  // Try to get HTML content first (preserves formatting)
                                                  let pastedContent = e.clipboardData.getData('text/html');
                                                  
                                                  if (pastedContent) {
                                                    // Clean up the HTML thoroughly
                                                    const tempDiv = document.createElement('div');
                                                    tempDiv.innerHTML = pastedContent;
                                                    
                                                    // Remove unwanted elements
                                                    tempDiv.querySelectorAll('meta, style, script, link, title, head').forEach(el => el.remove());
                                                    
                                                    // Remove spans with display:none
                                                    tempDiv.querySelectorAll('span').forEach(span => {
                                                      const style = span.getAttribute('style') || '';
                                                      if (style.includes('display: none') || style.includes('display:none')) {
                                                        span.remove();
                                                      } else if (span.innerHTML.trim() === '' && !span.querySelector('*')) {
                                                        // Remove empty spans
                                                        span.remove();
                                                      } else if (style.includes('--tw-')) {
                                                        // Remove Tailwind CSS variable spam, keep content
                                                        span.removeAttribute('style');
                                                      }
                                                    });
                                                    
                                                    // Clean excessive inline styles from all elements
                                                    tempDiv.querySelectorAll('*').forEach(el => {
                                                      const style = el.getAttribute('style') || '';
                                                      if (style.includes('--tw-')) {
                                                        // Remove the style entirely if it's mostly Tailwind variables
                                                        el.removeAttribute('style');
                                                      }
                                                    });
                                                    
                                                    // Get the cleaned HTML
                                                    pastedContent = tempDiv.innerHTML;
                                                  } else {
                                                    // Fall back to plain text, converting newlines to <br> and preserving structure
                                                    const plainText = e.clipboardData.getData('text/plain');
                                                    // Convert double newlines to paragraphs, single to <br>
                                                    pastedContent = plainText
                                                      .split(/\n\n+/)
                                                      .map(para => `<p>${para.replace(/\n/g, '<br>')}</p>`)
                                                      .join('');
                                                  }
                                                  
                                                  // Insert at cursor position
                                                  document.execCommand('insertHTML', false, pastedContent);
                                                }}
                                                onBlur={(e) => {
                                                  // Save HTML content on blur
                                                  const htmlContent = e.target.innerHTML;
                                                  if (htmlContent !== field.content) {
                                                    updateFormField(field.id, { content: htmlContent });
                                                  }
                                                  // Update cache to mark as synced
                                                  const cache = staticTextEditorCache.current;
                                                  cache.set(field.id, { 
                                                    content: htmlContent, 
                                                    initialized: true, 
                                                    hasUserEdits: false 
                                                  });
                                                }}
                                                onKeyDown={(e) => {
                                                  // Handle Tab for indentation
                                                  if (e.key === 'Tab') {
                                                    e.preventDefault();
                                                    if (e.shiftKey) {
                                                      document.execCommand('outdent', false, null);
                                                    } else {
                                                      document.execCommand('indent', false, null);
                                                    }
                                                  }
                                                }}
                                              />
                                              <p className="text-xs text-gray-500 mt-2">
                                                <strong>⌘S / Ctrl+S</strong> to save. Paste formatted text or use the toolbar below. Tab/Shift+Tab for indent/outdent.
                                              </p>
                                            </div>
                                            
                                            {/* Text Styling Options */}
                                            <div className="bg-gray-50 rounded-lg p-3 space-y-3">
                                              <Label className="text-xs font-semibold block">Formatting Toolbar (select text first)</Label>
                                              
                                              {/* Format Buttons Row */}
                                              <div className="flex items-center gap-2 flex-wrap">
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('bold', false, null)}
                                                  title="Bold"
                                                >
                                                  <Bold className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('italic', false, null)}
                                                  title="Italic"
                                                >
                                                  <Italic className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('underline', false, null)}
                                                  title="Underline"
                                                >
                                                  <Underline className="h-4 w-4" />
                                                </Button>
                                                
                                                <div className="w-px h-6 bg-gray-300 mx-1" />
                                                
                                                {/* List formatting buttons */}
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('insertUnorderedList', false, null)}
                                                  title="Bullet List"
                                                >
                                                  <List className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('insertOrderedList', false, null)}
                                                  title="Numbered List"
                                                >
                                                  <ListOrdered className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('indent', false, null)}
                                                  title="Increase Indent"
                                                >
                                                  <Indent className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('outdent', false, null)}
                                                  title="Decrease Indent"
                                                >
                                                  <Outdent className="h-4 w-4" />
                                                </Button>
                                                
                                                <div className="w-px h-6 bg-gray-300 mx-1" />
                                                
                                                {/* Alignment - uses execCommand for selected text/paragraph */}
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('justifyLeft', false, null)}
                                                  title="Align Left"
                                                >
                                                  <AlignLeft className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('justifyCenter', false, null)}
                                                  title="Align Center"
                                                >
                                                  <AlignCenter className="h-4 w-4" />
                                                </Button>
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => document.execCommand('justifyRight', false, null)}
                                                  title="Align Right"
                                                >
                                                  <AlignRight className="h-4 w-4" />
                                                </Button>
                                                
                                                <div className="w-px h-6 bg-gray-300 mx-1" />
                                                
                                                {/* Insert Link */}
                                                <Button
                                                  type="button"
                                                  variant="outline"
                                                  size="sm"
                                                  className="h-8 w-8 p-0"
                                                  onMouseDown={(e) => e.preventDefault()}
                                                  onClick={() => {
                                                    const selection = window.getSelection();
                                                    const selectedText = selection?.toString();
                                                    
                                                    if (!selectedText) {
                                                      alert('Please select some text first, then click the link button.');
                                                      return;
                                                    }
                                                    
                                                    let url = prompt('Enter URL (include https://):', 'https://');
                                                    if (url && url !== 'https://') {
                                                      // Ensure URL has protocol - add https:// if missing
                                                      if (!url.match(/^https?:\/\//i)) {
                                                        url = 'https://' + url.replace(/^\/+/, '');
                                                      }
                                                      document.execCommand('createLink', false, url);
                                                      // Style the link to be visible
                                                      const links = document.querySelectorAll(`[data-field-id="${field.id}"] a`);
                                                      links.forEach(link => {
                                                        link.style.color = '#2563eb';
                                                        link.style.textDecoration = 'underline';
                                                        link.setAttribute('target', '_blank');
                                                        link.setAttribute('rel', 'noopener noreferrer');
                                                      });
                                                    }
                                                  }}
                                                  title="Insert Link (select text first)"
                                                >
                                                  <Link className="h-4 w-4" />
                                                </Button>
                                              </div>
                                              
                                              {/* Color and Size Row */}
                                              <div className="flex items-center gap-4">
                                                <div className="flex items-center gap-2">
                                                  <Label className="text-xs">Text Color:</Label>
                                                  <input
                                                    type="color"
                                                    defaultValue="#000000"
                                                    onMouseDown={(e) => e.preventDefault()}
                                                    onChange={(e) => document.execCommand('foreColor', false, e.target.value)}
                                                    className="w-8 h-8 rounded cursor-pointer border"
                                                    title="Select text first, then pick a color"
                                                  />
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <Label className="text-xs">Selected Text Size:</Label>
                                                  <select
                                                    className="h-8 w-24 text-xs border rounded-md px-1 bg-white"
                                                    defaultValue=""
                                                    title="Highlight text in the editor first, then pick a size"
                                                    data-testid="static-selection-font-size"
                                                    onMouseDown={() => {
                                                      const sel = window.getSelection();
                                                      staticTextSelectionRef.current = sel && sel.rangeCount > 0 ? sel.getRangeAt(0).cloneRange() : null;
                                                    }}
                                                    onChange={(e) => {
                                                      const px = e.target.value;
                                                      e.target.value = '';
                                                      if (!px) return;
                                                      const saved = staticTextSelectionRef.current;
                                                      const editor = document.querySelector(`[data-field-id="${field.id}"][contenteditable]`) || document.querySelector(`[data-field-id="${field.id}"]`);
                                                      if (!saved || saved.collapsed || !editor || !editor.contains(saved.commonAncestorContainer)) {
                                                        toast.error('Highlight some text in the editor first, then pick a size');
                                                        return;
                                                      }
                                                      const sel = window.getSelection();
                                                      sel.removeAllRanges();
                                                      sel.addRange(saved);
                                                      document.execCommand('fontSize', false, '7');
                                                      editor.querySelectorAll('font[size="7"]').forEach(f => {
                                                        const span = document.createElement('span');
                                                        span.style.fontSize = `${px}px`;
                                                        while (f.firstChild) span.appendChild(f.firstChild);
                                                        f.replaceWith(span);
                                                      });
                                                      staticTextEditorCache.current.set(field.id, { content: editor.innerHTML, initialized: true, hasUserEdits: true });
                                                      updateFormField(field.id, { content: editor.innerHTML });
                                                      staticTextSelectionRef.current = null;
                                                    }}
                                                  >
                                                    <option value="">Size…</option>
                                                    <option value="10">10px</option>
                                                    <option value="12">12px</option>
                                                    <option value="14">14px</option>
                                                    <option value="16">16px</option>
                                                    <option value="18">18px</option>
                                                    <option value="20">20px</option>
                                                    <option value="24">24px</option>
                                                    <option value="28">28px</option>
                                                    <option value="32">32px</option>
                                                  </select>
                                                </div>
                                                <div className="flex items-center gap-2">
                                                  <Label className="text-xs" title="Default size for text without its own size">Base Size:</Label>
                                                  <Select
                                                    value={String(field.fontSize || 14)}
                                                    onValueChange={(value) => updateFormField(field.id, { fontSize: parseInt(value) })}
                                                  >
                                                    <SelectTrigger className="w-20 h-8">
                                                      <SelectValue />
                                                    </SelectTrigger>
                                                    <SelectContent>
                                                      <SelectItem value="10">10px</SelectItem>
                                                      <SelectItem value="12">12px</SelectItem>
                                                      <SelectItem value="14">14px</SelectItem>
                                                      <SelectItem value="16">16px</SelectItem>
                                                      <SelectItem value="18">18px</SelectItem>
                                                      <SelectItem value="20">20px</SelectItem>
                                                      <SelectItem value="24">24px</SelectItem>
                                                      <SelectItem value="28">28px</SelectItem>
                                                      <SelectItem value="32">32px</SelectItem>
                                                    </SelectContent>
                                                  </Select>
                                                </div>
                                              </div>
                                            </div>
                                            
                                            {/* Border & Image Options (Aug 2026) */}
                                            <div className="bg-gray-50 rounded-lg p-3 space-y-3" data-testid={`static-border-options-${field.id}`}>
                                              <Label className="text-xs font-semibold block">Border & Image Options</Label>
                                              <div className="flex items-center gap-4 flex-wrap">
                                                <label className="flex items-center text-xs">
                                                  <input
                                                    type="checkbox"
                                                    checked={field.borderEnabled || false}
                                                    onChange={(e) => updateFormField(field.id, { borderEnabled: e.target.checked })}
                                                    className="mr-1"
                                                    data-testid="static-border-toggle"
                                                  />
                                                  Border
                                                </label>
                                                {field.borderEnabled && (
                                                  <>
                                                    <div className="flex items-center gap-2">
                                                      <Label className="text-xs">Thickness:</Label>
                                                      <Select
                                                        value={String(field.borderThickness || 'thin')}
                                                        onValueChange={(v) => updateFormField(field.id, { borderThickness: v })}
                                                      >
                                                        <SelectTrigger className="w-28 h-8 text-xs" data-testid="static-border-thickness">
                                                          <SelectValue />
                                                        </SelectTrigger>
                                                        <SelectContent>
                                                          <SelectItem value="thin">Thin (1px)</SelectItem>
                                                          <SelectItem value="medium">Medium (2px)</SelectItem>
                                                          <SelectItem value="thick">Thick (4px)</SelectItem>
                                                          <SelectItem value="3">3px</SelectItem>
                                                          <SelectItem value="5">5px</SelectItem>
                                                          <SelectItem value="6">6px</SelectItem>
                                                          <SelectItem value="8">8px</SelectItem>
                                                        </SelectContent>
                                                      </Select>
                                                    </div>
                                                    <div className="flex items-center gap-2">
                                                      <Label className="text-xs">Color:</Label>
                                                      <input
                                                        type="color"
                                                        value={field.borderColor || '#d1d5db'}
                                                        onChange={(e) => updateFormField(field.id, { borderColor: e.target.value })}
                                                        className="w-8 h-8 rounded cursor-pointer border"
                                                        data-testid="static-border-color"
                                                      />
                                                      <span className="text-xs text-gray-600">{field.borderColor || '#d1d5db'}</span>
                                                    </div>
                                                  </>
                                                )}
                                              </div>
                                              <div className="flex items-center gap-2">
                                                <Label className="text-xs">Image Alignment (pasted images):</Label>
                                                <Select
                                                  value={field.imageAlign || 'left'}
                                                  onValueChange={(v) => updateFormField(field.id, { imageAlign: v })}
                                                >
                                                  <SelectTrigger className="w-24 h-8 text-xs" data-testid="static-image-align">
                                                    <SelectValue />
                                                  </SelectTrigger>
                                                  <SelectContent>
                                                    <SelectItem value="left">Left</SelectItem>
                                                    <SelectItem value="center">Center</SelectItem>
                                                    <SelectItem value="right">Right</SelectItem>
                                                  </SelectContent>
                                                </Select>
                                              </div>
                                            </div>
                                            
                                            {/* Variable Reference Card */}
                                            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                                              <div className="flex items-center gap-2 mb-2">
                                                <Info className="h-4 w-4 text-blue-600" />
                                                <span className="text-xs font-semibold text-blue-900">Available Variables</span>
                                              </div>
                                              <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs">
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_name}}`}</code>
                                                  <span className="text-gray-600">→ Full name</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_dob}}`}</code>
                                                  <span className="text-gray-600">→ Date of birth</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_first_name}}`}</code>
                                                  <span className="text-gray-600">→ First name</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_last_name}}`}</code>
                                                  <span className="text-gray-600">→ Last name</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_age}}`}</code>
                                                  <span className="text-gray-600">→ Age</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_SSNunmasked}}`}</code>
                                                  <span className="text-gray-600">→ SSN (full)</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_email}}`}</code>
                                                  <span className="text-gray-600">→ Email</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_phone}}`}</code>
                                                  <span className="text-gray-600">→ Phone</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_address}}`}</code>
                                                  <span className="text-gray-600">→ Full address</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_streetaddress}}`}</code>
                                                  <span className="text-gray-600">→ Street</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_city}}`}</code>
                                                  <span className="text-gray-600">→ City</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_state}}`}</code>
                                                  <span className="text-gray-600">→ State</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_zipcode}}`}</code>
                                                  <span className="text-gray-600">→ ZIP code</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{client_mrn}}`}</code>
                                                  <span className="text-gray-600">→ MRN</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{today}}`}</code>
                                                  <span className="text-gray-600">→ Current date</span>
                                                </div>
                                                <div className="flex items-center gap-1">
                                                  <code className="bg-white px-1 py-0.5 rounded text-blue-700">{`{{provider_name}}`}</code>
                                                  <span className="text-gray-600">→ Provider's name</span>
                                                </div>
                                              </div>
                                              <p className="text-xs text-gray-600 mt-2 italic">
                                                Example: "I, {`{{client_name}}`}, hereby consent to treatment on {`{{today}}`}."
                                              </p>
                                            </div>
                                          </div>
                                        )}
                                        
                                        {field.type === 'divider' && (
                                          <div className="grid grid-cols-2 gap-2">
                                            <div>
                                              <Label className="text-xs">Thickness (px)</Label>
                                              <Input
                                                type="number"
                                                min="1"
                                                max="10"
                                                value={field.thickness || 1}
                                                onChange={(e) => updateFormField(field.id, { thickness: parseInt(e.target.value) })}
                                                className="h-8"
                                              />
                                            </div>
                                            <div>
                                              <Label className="text-xs">Color</Label>
                                              <Input
                                                type="color"
                                                value={field.color || '#cccccc'}
                                                onChange={(e) => updateFormField(field.id, { color: e.target.value })}
                                                className="h-8 p-1"
                                              />
                                            </div>
                                          </div>
                                        )}
                                        
                                        {field.type === 'spacer' && (
                                          <div>
                                            <Label className="text-xs">Height (pixels)</Label>
                                            <Input
                                              type="number"
                                              min="5"
                                              max="200"
                                              value={field.height || 20}
                                              onChange={(e) => updateFormField(field.id, { height: parseInt(e.target.value) })}
                                              className="h-8"
                                            />
                                          </div>
                                        )}
                                        
                                        {field.type === 'container' && (
                                          <div className="grid grid-cols-2 gap-2">
                                            <div>
                                              <Label className="text-xs">Padding (px)</Label>
                                              <Input
                                                type="number"
                                                min="0"
                                                max="50"
                                                value={field.padding || 16}
                                                onChange={(e) => updateFormField(field.id, { padding: parseInt(e.target.value) })}
                                                className="h-8"
                                              />
                                            </div>
                                            <div>
                                              <Label className="text-xs">Border Radius (px)</Label>
                                              <Input
                                                type="number"
                                                min="0"
                                                max="20"
                                                value={field.borderRadius || 8}
                                                onChange={(e) => updateFormField(field.id, { borderRadius: parseInt(e.target.value) })}
                                                className="h-8"
                                              />
                                            </div>
                                          </div>
                                        )}
                                        
                                        {field.type === 'signature' && (
                                          <div className="text-xs text-gray-600 mt-1">
                                            ✍️ Digital signature capture field - clients will sign electronically
                                          </div>
                                        )}
                                            </SortableFieldItem>
                                          </div>
                                        );
                                      })
                                    )}
                                  </div>
                                  
                                  {/* Add Field to Row Button - on the right */}
                                  <Button
                                    type="button"
                                    variant="ghost"
                                    size="sm"
                                    className="text-xs text-gray-400 hover:text-gray-600 px-2 h-8 flex-shrink-0 self-center"
                                    onClick={() => {
                                      toast.info(`Drag a field into this row, or click a field type to add it`);
                                    }}
                                    title="Add field to this row"
                                  >
                                    +
                                  </Button>
                                  </div>
                                </DroppableRow>
                              ))}
                              
                              {/* Add New Row Button */}
                              <div className="relative">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  className="w-full mt-2 border-dashed text-gray-500 hover:border-blue-400 hover:text-blue-600"
                                  onClick={() => {
                                    // Left click: add to end of this section
                                    addFormField({ type: 'row_placeholder', label: 'Empty Row', icon: '⊡', category: 'layout' }, section.header.id);
                                    toast.success('Empty row added to section!');
                                  }}
                                >
                                  + Add New Row
                                </Button>
                              </div>
                            </div>
                          </SortableSection>
                        ))}
                      </div>
                    </SortableContext>
                  </DndContext>
                                )}
              </div>
            </div>
          ) : null}
                          
                          <div className="sticky bottom-0 z-20 bg-white border-t -mx-6 px-6 py-3 -mb-6 mt-4 flex justify-end space-x-2" data-testid="form-builder-footer-actions">
                            <Button type="button" variant="outline" onClick={() => {
                              handleOpenChange(false);
                              setActiveFormFields([]);
                              setEditingFormTemplate(null);
                              setFormTemplateData({
                                name: '',
                                description: '',
                                form_type: '',
                                form_fields: []
                              });
                              // Return to the Forms Management dialog instead of closing completely
                              if (onCancelToManagement) onCancelToManagement();
                            }}>
                              Cancel
                            </Button>
                            <Button 
                              type="button" 
                              variant="outline"
                              className="bg-blue-50 hover:bg-blue-100 text-blue-700 border-blue-300"
                              onClick={async () => {
                                try {
                                  const templateData = {
                                    ...formTemplateData,
                                    form_fields: syncLegacyAssessmentKeys(activeFormFields),
                                    status_notifications: (formTemplateData.status_notifications || []).filter(r => r.status && (r.user_ids || []).length > 0)
                                  };
                                  
                                  if (editingFormTemplate) {
                                    // Update existing form template without closing
                                    await axios.put(`${API}/form-templates/${editingFormTemplate.id}`, templateData);
                                    // Optimistically update local state
                                    setFormTemplates(prev => prev.map(t => 
                                      t.id === editingFormTemplate.id ? { ...t, ...templateData } : t
                                    ));
                                    toast.success("Progress saved! You can continue editing.");
                                  } else {
                                    // Create new template and switch to edit mode
                                    const response = await axios.post(`${API}/form-templates`, templateData);
                                    const newTemplate = response.data;
                                    setEditingFormTemplate(newTemplate);
                                    // Optimistically add to local state
                                    setFormTemplates(prev => [...prev, newTemplate]);
                                    toast.success("Template created! Continue editing or click 'Done' to close.");
                                  }
                                  
                                  // Refresh data in background without closing dialog
                                  // Static data - no global refresh needed
                                } catch (error) {
                                  toast.error(error.response?.data?.detail || "Failed to save progress");
                                }
                              }}
                              disabled={activeFormFields.length === 0}
                            >
                              <Save className="h-4 w-4 mr-2" />
                              Save Progress
                            </Button>
                            <Button 
                              type="submit" 
                              data-testid="create-form-template-submit" 
                              className="bg-teal-600 hover:bg-teal-700"
                              disabled={activeFormFields.length === 0}
                            >
                              {editingFormTemplate ? 'Done' : 'Create & Close'}
                            </Button>
                          </div>
                        </form>
                      </DialogContent>
                      {showFormPreview && (
        <FormTemplatePreviewDialog
          onClose={() => setShowFormPreview(false)}
          templateData={formTemplateData}
          fields={buildPreviewFields()}
          previewMode={previewMode}
          setPreviewMode={setPreviewMode}
        />
      )}
    </Dialog>

    <TaskTypeManagerDialog
      open={showTaskTypeManager}
      onOpenChange={setShowTaskTypeManager}
      users={users}
      onSaved={fetchTaskTypes}
    />
    </>
  );
}
